import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { landerOrigin, pageLink, publicLink, publicOrigin } from "@/lib/landerDomain";
import {
  Broadcast, Copy, Eye, ArrowSquareOut, ChartBar, ChartLineUp, ShuffleAngular, Lightning,
  Stack, HardDrives, Trash, ArrowCounterClockwise, PaperPlaneTilt, Prohibit,
  Plus, TestTube,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ExclusivityRules from "@/components/ExclusivityRules";
import PitchToLiveModal from "@/components/PitchToLiveModal";
import SamplePreviewLightbox from "@/components/SamplePreviewLightbox";
import PitchAnalyticsPanel from "@/components/PitchAnalyticsPanel";
import { verticalLabel, verticalColor, VERTICAL_PRESETS, VERTICAL_ORDER } from "@/lib/verticalPresets";
import { TCPA_LANGUAGE } from "@/pages/landing/config";
import { useNavigate, useSearchParams } from "react-router-dom";
import NewDestinationModal from "@/components/delivery/NewDestinationModal";
import BuyerCombobox from "@/components/shared/BuyerCombobox";
import { GlobalFunnelsSection } from "@/components/GlobalFunnelsSection";
import { ProspectPreviewModal } from "@/components/landing/ProspectPreviewModal";
import { DeletedPagesModal, DeletePageButton } from "@/components/landing/DeletedPagesModal";
import { TfCoverageCard } from "@/components/trustedform/TfCoverageCard";
import { LegalPagesSection } from "@/components/legal/LegalPagesSection";
import { MediaBuyerLinksCard } from "@/components/results/MediaBuyerLinksCard";
import { NewFromTemplateDialog } from "@/components/landers/NewFromTemplateDialog";
import { useTrackingCode, withCode } from "@/lib/useTrackingCode";
import { PixelBadge } from "@/components/pixels/PixelBadge";

/**
 * Iter-34: Admin overview of every landing page on the platform.
 *
 * Gives admins a single place to:
 *   - See every landing page URL at a glance (slug + human label + preview)
 *   - Copy the public URL or a vendor-attributed tracking URL to their
 *     clipboard in one click (with an affiliate selector at the top).
 *   - See platform-wide submitted / accepted / rejected counts for each
 *     page over the last 30 days.
 *   - Open a preview (with `?preview=1`) directly in a new tab.
 *
 * Everything here is READ-ONLY. The A/B toggle lives per-affiliate on the
 * Manage Affiliate modal; page copy edits live in `config.js`. This page
 * is the "control tower" — it doesn't own writes.
 */

// The 6 direct landers + the /lp/pi split router. Order + labels
// intentionally mirror /app/frontend/src/pages/landing/config.js so the
// list matches what leads actually see.
const LANDERS = [
  { slug: "personal-injury",     label: "Personal Injury (all-in-one)", tag: "PI · dropdown", tagColor: "#f1b62c" },
  { slug: "free-case-review",    label: "Free Case Review (all-in-one)", tag: "PI · dropdown", tagColor: "#f1b62c" },
  { slug: "auto-accident",       label: "Auto Accident",                 tag: "MVA",           tagColor: "#d92f2f" },
  { slug: "truck-accident",      label: "Truck / 18-Wheeler",            tag: "MVA",           tagColor: "#d92f2f" },
  { slug: "rideshare-accident",  label: "Rideshare (Uber / Lyft)",       tag: "MVA",           tagColor: "#d92f2f" },
  { slug: "motorcycle-accident", label: "Motorcycle",                    tag: "MVA",           tagColor: "#d92f2f" },
];

const SPLIT_ROUTER = {
  slug: "pi",
  label: "PI Split Router",
  tag: "A/B · sticky bucket",
  tagColor: "#059669",
};

function fmtPct(n) { return `${Math.round((n || 0) * 100)}%`; }

function copy(text) {
  if (!navigator.clipboard) {
    // Fallback for insecure contexts — build a hidden textarea.
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } else {
    navigator.clipboard.writeText(text);
  }
  toast.success("Copied to clipboard");
}

export default function AdminLandingPages() {
  const { user } = useAuth();
  const isMediaBuyer = user?.role === "media_buyer";
  const mbCode = useTrackingCode(isMediaBuyer);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAff, setSelectedAff] = useState(""); // public_source_id
  const [subId, setSubId] = useState("");
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);

  const origin = publicOrigin();

  // Load affiliates (for the tracking-URL builder) + platform-wide stats.
  useEffect(() => {
    if (isMediaBuyer) return;  // media buyers don't get vendor picker or platform stats
    let alive = true;
    api.get("/organizations", { params: { type: "affiliate" } })
      .then((r) => {
        if (!alive) return;
        // API returns either an array or {items:[]} — normalise.
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.organizations || []);
        setAffiliates(list.filter((o) => o.type === "affiliate" && o.status !== "archived"));
      })
      .catch(() => { /* leave list empty, non-blocking */ });
    return () => { alive = false; };
  }, [isMediaBuyer]);

  useEffect(() => {
    if (isMediaBuyer) return;  // /api/admin/landing-pages/stats is admin-only
    let alive = true;
    api.get(`/admin/landing-pages/stats?days=${days}`)
      .then((r) => { if (alive) setStats(r.data); })
      .catch((e) => {
        if (alive) toast.error(e.response?.data?.detail || "Failed to load stats");
      });
    return () => { alive = false; };
  }, [days, isMediaBuyer]);

  const affLookup = useMemo(() => {
    const m = {};
    for (const a of affiliates) if (a.public_source_id) m[a.public_source_id] = a;
    return m;
  }, [affiliates]);
  const selectedAffObj = selectedAff ? affLookup[selectedAff] : null;

  const buildUrl = (slug) => {
    const params = new URLSearchParams();
    if (selectedAff) params.set("aff", selectedAff);
    if (subId.trim()) params.set("sub", subId.trim());
    const qs = params.toString();
    return `${origin}/lp/${slug}${qs ? `?${qs}` : ""}`;
  };

  const totals = useMemo(() => {
    if (!stats?.pages) return { submitted: 0, accepted: 0, rejected: 0, recent_24h: 0 };
    return stats.pages.reduce(
      (acc, p) => ({
        submitted: acc.submitted + p.submitted,
        accepted: acc.accepted + p.accepted,
        rejected: acc.rejected + p.rejected,
        recent_24h: acc.recent_24h + p.recent_24h,
      }),
      { submitted: 0, accepted: 0, rejected: 0, recent_24h: 0 },
    );
  }, [stats]);

  const statFor = (slug) => (stats?.pages || []).find((p) => p.landing_page === slug);

  return (
    <div className="p-2" data-testid="admin-landing-pages">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Broadcast size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">
          Landing pages
        </h1>
        <div className="label-mono ml-2">
          {isMediaBuyer
            ? "Read-only · attach pixels via per-page overrides"
            : `Public consumer intake · ${LANDERS.length} verticals + 1 split router`}
        </div>
      </div>

      {!isMediaBuyer && <TfCoverageCard />}
      {isMediaBuyer && <MediaBuyerLinksCard code={mbCode} origin={origin} />}

      {/* Platform-wide totals strip — admins only (media buyers use /media-buyer/attribution). */}
      {!isMediaBuyer && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="brut-card p-4" data-testid="lp-total-submitted">
          <div className="label-mono">Submitted (last {days}d)</div>
          <div className="font-chivo font-black text-3xl mt-1">{totals.submitted}</div>
        </div>
        <div className="brut-card p-4" data-testid="lp-total-accepted">
          <div className="label-mono">Accepted</div>
          <div className="font-chivo font-black text-3xl mt-1 text-emerald-800">{totals.accepted}</div>
        </div>
        <div className="brut-card p-4" data-testid="lp-total-rejected">
          <div className="label-mono">Rejected</div>
          <div className="font-chivo font-black text-3xl mt-1 text-rose-800">{totals.rejected}</div>
        </div>
        <div className="brut-card p-4 bg-black text-white" data-testid="lp-total-24h">
          <div className="label-mono opacity-70">Last 24 hours</div>
          <div className="font-chivo font-black text-3xl mt-1">{totals.recent_24h}</div>
        </div>
      </div>
      )}

      {/* Iter-35: Exclusivity rules manager */}
      {!isMediaBuyer && <ExclusivityRules />}

      {/* Iter-43: Custom buyer landing pages (admin CMS) */}
      {/* Iter-62.2.1: First-class Landing Page Experiences section — makes
          Standard, Quiz, and Focused Funnel equally discoverable next to
          the six legacy built-in landers below. Hidden for media buyers
          who use the masked path. */}
      {!isMediaBuyer && <GlobalFunnelsSection affiliates={affiliates} origin={origin} />}
      {!isMediaBuyer && <LegalPagesSection />}
      {!isMediaBuyer && <ExperiencesSection />}
      <CustomBuyerPagesSection isMediaBuyer={isMediaBuyer} />

      {/* Iter-48: Photo Retention — admin visibility + manual sweep */}
      {!isMediaBuyer && <PhotoRetentionSection />}

      {/* Iter-54.2: Sample Submissions Dashboard — last 20 demo submits */}
      {!isMediaBuyer && <SampleSubmissionsSection />}

      {/* Tracking-URL builder — hidden for media buyers (vendor list is admin-only) */}
      {!isMediaBuyer && (
      <div className="brut-card p-5 mb-6" data-testid="lp-url-builder"><div className="flex items-center gap-2 mb-3">
          <Lightning size={14} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter">
            Tracking URL builder
          </div>
          <div className="label-mono ml-auto">
            Pick an affiliate + optional sub_id to auto-fill every URL below
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3 items-center">
          <div>
            <label className="label-mono">Attribute to affiliate</label>
            <select
              value={selectedAff}
              onChange={(e) => setSelectedAff(e.target.value)}
              data-testid="lp-aff-select"
              className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
            >
              <option value="">— No affiliate (direct traffic) —</option>
              {affiliates.map((a) => (
                <option key={a.id} value={a.public_source_id || ""}>
                  {a.name} {a.public_source_id ? `(${a.public_source_id})` : "(no source_id yet)"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-mono">sub_id (optional)</label>
            <input
              value={subId}
              onChange={(e) => setSubId(e.target.value)}
              placeholder="fb-adset-1"
              data-testid="lp-sub-input"
              className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
            />
          </div>
          <div>
            <label className="label-mono">Window</label>
            <div className="flex gap-1 mt-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`lp-window-${d}`}
                  className={`border border-black px-2 py-2 text-[10px] uppercase tracking-widest ${
                    days === d ? "bg-black text-white" : "hover:bg-zinc-100"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>
        {selectedAffObj && (
          <div className="mt-3 border border-black bg-amber-50 p-3" data-testid="lp-selected-vendor">
            <div className="label-mono">Selected affiliate</div>
            <div className="text-sm font-bold">{selectedAffObj.name}</div>
            <div className="text-[11px] font-mono text-zinc-600">
              public_source_id: {selectedAffObj.public_source_id} · status:{" "}
              {selectedAffObj.status}
              {selectedAffObj.ab_split_enabled && (
                <span className="ml-2 bg-emerald-700 text-white px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                  A/B on
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Split router callout — hidden for media buyers */}
      {!isMediaBuyer && (
      <div className="brut-card mb-6" data-testid={`lp-card-${SPLIT_ROUTER.slug}`}>
        <div className="border-b border-black bg-emerald-50 px-4 py-3 flex items-center gap-2">
          <ShuffleAngular size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter">
            {SPLIT_ROUTER.label}
          </div>
          <span
            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-black"
            style={{ background: SPLIT_ROUTER.tagColor, color: "#fff" }}
          >
            {SPLIT_ROUTER.tag}
          </span>
          <div className="label-mono ml-auto">
            /lp/pi — bucketed 50/50 when the affiliate has A/B enabled
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
          <div className="font-mono text-[13px] break-all">{buildUrl("pi")}</div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => copy(buildUrl("pi"))}
              data-testid={`lp-copy-${SPLIT_ROUTER.slug}`}
              className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1.5"
            >
              <Copy size={11} weight="bold" /> Copy
            </button>
            <a
              href={buildUrl("pi")}
              target="_blank"
              rel="noreferrer"
              data-testid={`lp-open-${SPLIT_ROUTER.slug}`}
              className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1.5"
            >
              <ArrowSquareOut size={11} weight="bold" /> Open
            </a>
          </div>
        </div>
      </div>
      )}

      {/* Grid of the 6 direct landers — admins only */}
      {!isMediaBuyer && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lp-grid">
        {LANDERS.map((lp) => {
          const s = statFor(lp.slug);
          const publicUrl = `${origin}/lp/${lp.slug}`;
          const trackingUrl = buildUrl(lp.slug);
          const previewUrl = `${origin}/lp/${lp.slug}?preview=1`;
          return (
            <div
              key={lp.slug}
              className="brut-card overflow-hidden"
              data-testid={`lp-card-${lp.slug}`}
            >
              <div className="border-b border-black px-4 py-3 flex items-center gap-2">
                <div className="font-chivo font-black uppercase tracking-tighter">
                  {lp.label}
                </div>
                <span
                  className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-black"
                  style={{ background: lp.tagColor, color: "#fff" }}
                >
                  {lp.tag}
                </span>
                <div className="label-mono ml-auto">/lp/{lp.slug}</div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 border-b border-black text-center">
                <div className="p-3 border-r border-black">
                  <div className="label-mono">Submitted</div>
                  <div className="font-chivo font-black text-xl">
                    {s?.submitted ?? 0}
                  </div>
                </div>
                <div className="p-3 border-r border-black">
                  <div className="label-mono">Accepted</div>
                  <div className="font-chivo font-black text-xl text-emerald-800">
                    {s?.accepted ?? 0}
                  </div>
                </div>
                <div className="p-3 border-r border-black">
                  <div className="label-mono">Rate</div>
                  <div className="font-chivo font-black text-xl">
                    {fmtPct(s?.acceptance_rate)}
                  </div>
                </div>
                <div className="p-3">
                  <div className="label-mono">24h</div>
                  <div className="font-chivo font-black text-xl">
                    {s?.recent_24h ?? 0}
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div>
                  <div className="label-mono mb-1">
                    {selectedAff ? "Affiliate-attributed URL" : "Public URL"}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 font-mono text-[12px] break-all border border-black bg-zinc-50 px-2 py-1.5"
                      data-testid={`lp-url-${lp.slug}`}
                    >
                      {selectedAff ? trackingUrl : publicUrl}
                    </div>
                    <button
                      onClick={() => copy(selectedAff ? trackingUrl : publicUrl)}
                      data-testid={`lp-copy-${lp.slug}`}
                      className="border border-black px-2 py-1.5 hover:bg-black hover:text-white transition-colors"
                      title="Copy URL"
                    >
                      <Copy size={12} weight="bold" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={selectedAff ? trackingUrl : publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`lp-open-${lp.slug}`}
                    className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1.5"
                  >
                    <ArrowSquareOut size={11} weight="bold" /> Open live
                  </a>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`lp-preview-${lp.slug}`}
                    className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1.5"
                  >
                    <Eye size={11} weight="bold" /> Preview mode
                  </a>
                  {s?.sandbox > 0 && (
                    <span
                      className="text-[10px] uppercase tracking-widest bg-amber-100 border border-amber-800 text-amber-900 px-2 py-1"
                      data-testid={`lp-sandbox-count-${lp.slug}`}
                    >
                      {s.sandbox} sandbox
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      <div className="mt-6 text-[11px] text-zinc-500 leading-relaxed max-w-3xl">
        <ChartBar size={11} weight="bold" className="inline mr-1" />
        Per-affiliate conversion breakdown lives on each affiliate&apos;s own
        dashboard (they see it when they log in as an affiliate). Per-lead audit
        detail is on the individual lead page. Copy on any landing page is
        edited in{" "}
        <code className="font-mono bg-zinc-100 px-1">
          /app/frontend/src/pages/landing/config.js
        </code>
        .
      </div>
    </div>
  );
}

/**
 * Iter-43: Custom buyer landing pages section (admin CMS).
 *
 * Sits inside AdminLandingPages just below Exclusivity Rules. Lists every
 * custom page with slug + firm + owner destination + status pill. Clicking
 * a row jumps to the full editor at /admin/landing-pages/custom/:id.
 */
/**
 * Iter-43.2: Per-custom-page conversion sparkline.
 *
 * `stats` shape: { submitted, accepted, acceptance_rate, recent_24h,
 *                  daily: [{date, submitted, accepted}] }
 *
 * Bar height ∝ daily submitted count (grey), emerald overlay ∝ accepted.
 * A run of tall grey bars = the page pulls leads but they don't qualify
 * (nudge the copy or the buyer's acceptance criteria). Short green =
 * healthy conversion. Empty axis = the page has no traffic yet.
 */
function ConversionSparkline({ stats, testId, width = 140, height = 32 }) {
  const daily = stats?.daily || [];
  const submitted = stats?.submitted || 0;
  const accepted = stats?.accepted || 0;
  const pct = submitted ? Math.round((accepted / submitted) * 100) : null;

  if (!daily.length || submitted === 0) {
    return (
      <div className="flex items-center gap-2" data-testid={testId}>
        <span className="text-[10px] text-zinc-400 font-mono">no traffic yet</span>
      </div>
    );
  }

  const maxSubmitted = Math.max(1, ...daily.map((d) => d.submitted || 0));
  const gap = 1;
  const barW = Math.max(1, (width - gap * (daily.length - 1)) / daily.length);
  const label = `30d: ${submitted} submitted, ${accepted} accepted (${pct ?? 0}%)`;

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
        className="align-middle"
      >
        <title>{label}</title>
        <line x1="0" y1={height - 0.5} x2={width} y2={height - 0.5}
              stroke="#e4e4e7" strokeWidth="1" />
        {daily.map((d, i) => {
          const s = d.submitted || 0;
          const a = Math.min(d.accepted || 0, s);
          if (!s) {
            return (
              <rect key={d.date || i} x={i * (barW + gap)} y={height - 1}
                    width={barW} height={1} fill="#e4e4e7" />
            );
          }
          const totalH = Math.max(1, Math.round((s / maxSubmitted) * (height - 2)));
          const acceptH = s ? Math.round((a / s) * totalH) : 0;
          return (
            <g key={d.date || i} transform={`translate(${i * (barW + gap)}, 0)`}>
              <rect x="0" y={height - totalH} width={barW} height={totalH} fill="#a1a1aa" />
              {acceptH > 0 && (
                <rect x="0" y={height - acceptH} width={barW} height={acceptH} fill="#059669" />
              )}
            </g>
          );
        })}
      </svg>
      <div className="text-[10px] font-mono leading-tight">
        <div>
          <span className="font-bold">{submitted}</span>
          <span className="text-zinc-500"> sub</span>
        </div>
        <div className={pct != null && pct >= 60 ? "text-emerald-700" : (pct != null && pct >= 30 ? "text-amber-700" : "text-red-700")}>
          <span className="font-bold">{accepted}</span>
          <span className="text-zinc-500"> ({pct ?? 0}%)</span>
        </div>
      </div>
    </div>
  );
}

function ExperiencesSection() {
  const nav = useNavigate();
  const origin = publicOrigin();
  const [, setSearchParams] = useSearchParams();
  const [summary, setSummary] = React.useState({
    standard: 0, quiz: 0, focused_funnel: 0, total: 0, loaded: false,
  });

  // Iter-62.2.1: full-dataset counts — count_documents per experience so
  // the header numbers reflect every matching row, never the current 25.
  React.useEffect(() => {
    api.get("/admin/custom-landing-pages/experience-summary")
      .then((r) => setSummary({
        standard: r.data?.standard || 0,
        quiz: r.data?.quiz || 0,
        focused_funnel: r.data?.focused_funnel || 0,
        total: r.data?.total || 0,
        loaded: true,
      }))
      .catch(() => setSummary((s) => ({ ...s, loaded: true })));
  }, []);

  // Iter-62.2.1: keep a per-experience representative sample slug so the
  // "Preview representative" button on each card can jump to a real live
  // example without the admin picking one manually. Skips silently if
  // nothing exists — the card stays informative but the button hides.
  const [rep, setRep] = React.useState({
    standard: null, quiz: null, focused_funnel: null,
  });
  React.useEffect(() => {
    ["standard", "quiz", "focused_funnel"].forEach((exp) => {
      api.get(
        `/admin/custom-landing-pages?form_experience=${exp}&status=published&page_size=25`,
      )
        .then((r) => {
          const first = (r.data?.items || [])[0];
          if (first?.slug) setRep((m) => ({ ...m, [exp]: first.slug }));
        })
        .catch(() => {});
    });
  }, []);

  const scrollToCustom = () => {
    const el = document.getElementById("custom-landing-pages-anchor");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const viewPages = (exp) => {
    // Push into the URL — CustomBuyerPagesSection reads ?custom_exp and
    // syncs its experience filter to it.
    setSearchParams({ custom_exp: exp });
    setTimeout(scrollToCustom, 40);
  };

  const createPage = (exp) => {
    nav(`/admin/landing-pages/custom/new?experience=${exp}`);
  };

  const CARDS = [
    {
      key: "standard",
      title: "Standard Form",
      badge: "Standard",
      description: "Traditional long-form intake — every field visible on one screen. Highest information density; best when the prospect knows what they need.",
      count: summary.standard,
      empty: "Every non-Quiz / non-Focused-Funnel page counts as Standard.",
      accent: "border-zinc-800 bg-zinc-50",
      badgeCls: "bg-zinc-100 text-zinc-900 border-zinc-800",
    },
    {
      key: "quiz",
      title: "Quiz-Style Form",
      badge: "Quiz",
      description: "Guided, question-by-question intake. One decision at a time. Typically lifts completion on cold traffic and unfamiliar case types.",
      count: summary.quiz,
      empty: "No Quiz pages yet — Create one",
      accent: "border-violet-800 bg-violet-50",
      badgeCls: "bg-violet-100 text-violet-900 border-violet-800",
    },
    {
      key: "focused_funnel",
      title: "Focused Funnel",
      badge: "Focused Funnel",
      description: "Streamlined focused-conversion funnel with a single primary action. Best for high-intent traffic where the ask is unambiguous.",
      count: summary.focused_funnel,
      empty: "No Focused Funnel pages yet — Create one",
      accent: "border-sky-800 bg-sky-50",
      badgeCls: "bg-sky-100 text-sky-900 border-sky-800",
    },
  ];

  return (
    <div className="brut-card mb-6" data-testid="admin-lp-experiences-section">
      <div className="px-4 py-3 border-b border-black flex items-center gap-3 flex-wrap">
        <div className="font-chivo font-black uppercase tracking-tighter text-sm">
          Landing Page Experiences
        </div>
        <div className="label-mono text-zinc-600">
          Every custom landing page uses one of these three form experiences. Counts reflect the full dataset — not the current page.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4"
           data-testid="admin-lp-experiences-grid">
        {CARDS.map((c) => (
          <div
            key={c.key}
            className={`border-2 p-4 flex flex-col gap-3 ${c.accent}`}
            data-testid={`admin-lp-experience-card-${c.key}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${c.badgeCls}`}
                data-testid={`admin-lp-experience-badge-${c.key}`}
              >
                {c.badge}
              </span>
              <div className="font-chivo font-black text-base leading-tight ml-auto text-right">
                <span data-testid={`admin-lp-experience-count-${c.key}`}>
                  {summary.loaded ? c.count : "—"}
                </span>
                <span className="label-mono text-zinc-600 ml-1">pages</span>
              </div>
            </div>
            <div className="font-chivo font-black text-lg leading-tight">
              {c.title}
            </div>
            <div className="text-xs text-zinc-700 leading-snug">
              {c.description}
            </div>
            {summary.loaded && c.count === 0 && (
              <div
                className="label-mono text-zinc-600"
                data-testid={`admin-lp-experience-empty-${c.key}`}
              >
                {c.empty}
              </div>
            )}
            {/* Iter-62.2.2: representative Public URL + Copy + Open Live
                — visible only when a published example exists so admins
                can practically test/share the experience without
                inventing a fake /lp/quiz route. */}
            {rep[c.key] && (
              <div
                className="border border-black bg-white px-2 py-1.5 text-[10px] font-mono break-all"
                data-testid={`admin-lp-experience-public-url-${c.key}`}
                title="Public URL of the representative published page"
              >
                {origin}/lp/{rep[c.key]}
              </div>
            )}
            <div className="mt-auto flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={() => viewPages(c.key)}
                data-testid={`admin-lp-experience-view-${c.key}`}
                className="border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
              >
                View pages
              </button>
              <button
                type="button"
                onClick={() => createPage(c.key)}
                data-testid={`admin-lp-experience-create-${c.key}`}
                className="border-2 border-black bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:border-emerald-700"
              >
                Create {c.badge} page
              </button>
              {rep[c.key] && (
                <>
                  <button
                    type="button"
                    onClick={() => copy(`${origin}/lp/${rep[c.key]}`)}
                    data-testid={`admin-lp-experience-copy-${c.key}`}
                    className="border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                    title="Copy the representative page's public URL"
                  >
                    Copy URL
                  </button>
                  <a
                    href={publicLink(`/lp/${rep[c.key]}`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-lp-experience-open-live-${c.key}`}
                    className="border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                    title="Open the real public URL (never preview=1)"
                  >
                    Open live
                  </a>
                  <a
                    href={publicLink(`/lp/${rep[c.key]}?preview=1`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-lp-experience-preview-${c.key}`}
                    className="border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                    title="Preview a representative published example (?preview=1 — no lead created)"
                  >
                    Preview
                  </a>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomBuyerPagesSection({ isMediaBuyer = false }) {
  const { user, hasPerm } = useAuth();
  const mbCode = useTrackingCode(isMediaBuyer);
  const nav = useNavigate();
  const [pages, setPages] = React.useState([]);
  const [analytics, setAnalytics] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [duplicating, setDuplicating] = React.useState(null);
  const [showBulk, setShowBulk] = React.useState(false);
  const [showTpl, setShowTpl] = React.useState(false);
  const [showPing, setShowPing] = React.useState(false);
  // Iter-50: which custom LP has its Share-Preview modal open (null = closed).
  const [sharing, setSharing] = React.useState(null);
  // Iter-50.1: "Share ALL previews" modal — mints one token per page and
  // renders every landing + Thank-You URL in one place so the admin can
  // send a full deck to a prospective client in one paste.
  const [bulkSharing, setBulkSharing] = React.useState(false);
  // Iter-51: per-page launch-readiness map — {page_id: {ready, blocker_count, ...}}
  const [readinessMap, setReadinessMap] = React.useState({});
  // Iter-53.1d: personalise-sample inputs — when filled, the Copy URLs
  // and Send Buyer Pitch buttons append `?firm=…&phone=…` to every
  // sample URL so each pitched buyer sees THEIR firm name + phone on
  // the sample pages. Real-client pages (Vance) never receive these
  // params because CustomLandingPage only honours them for `sample-*`
  // slugs.
  const [pitchFirm, setPitchFirm] = React.useState("");
  const [pitchPhone, setPitchPhone] = React.useState("");
  // Iter-62.2: search / filter / pagination — server-side, scoped to
  // the Custom Landing Pages manager. Media Buyer path still calls
  // `/media-buyer/landing-pages` and is unaffected.
  const [searchTerm, setSearchTerm] = React.useState("");
  const [expFilter, setExpFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [buyerFilter, setBuyerFilter] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  // Iter-75.8 — bumped after "Restore samples" fires so the samples
  // useEffect re-fetches from the server. Increments on success only.
  const [samplesReloadTick, setSamplesReloadTick] = React.useState(0);
  const [restoringSamples, setRestoringSamples] = React.useState(false);
  // Iter-75.10 — Pitch-to-Live modal target (null = closed) and
  // Sample Deck lightbox toggle.
  const [pitchTarget, setPitchTarget] = React.useState(null);
  const [showDeck, setShowDeck] = React.useState(false);
  // Iter-75.11 — Pitch analytics modal + per-row rollback busy flag.
  const [showPitchAnalytics, setShowPitchAnalytics] = React.useState(false);
  const [rollingBack, setRollingBack] = React.useState(null);
  // Iter-62.2: separate unfiltered sample-slugs fetch so the "Copy Sample
  // URLs" and "Send Buyer Pitch" buttons keep finding every sample even
  // when the manager is filtered/paginated to a different view. Legacy
  // list endpoint (no params) returns the pre-Iter-62.2 shape.
  const [samplePages, setSamplePages] = React.useState([]);
  const [showProspect, setShowProspect] = React.useState(false);
  const [showTrash, setShowTrash] = React.useState(false);
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const canShare = !isMediaBuyer && hasPerm("landing_pages.share_previews");

  const load = React.useCallback(() => {
    setLoading(true);
    if (isMediaBuyer) {
      // Media buyer uses the dedicated masked endpoint. Attribution counts
      // are baked in per-page as `attribution_30d`.
      api.get("/media-buyer/landing-pages")
        .then((r) => {
          const rows = r.data?.pages || [];
          setPages(rows);
          setTotal(rows.length);
          const a = {};
          for (const p of rows) {
            const c = p.attribution_30d || {};
            a[p.id] = {
              submitted: c.submitted || 0,
              accepted: c.accepted || 0,
              acceptance_rate: c.acceptance_rate || 0,
              recent_24h: c.recent_24h || 0,
              daily: [],  // sparkline detail hidden — attribution page has the drill-down
            };
          }
          setAnalytics(a);
        })
        .catch((e) => toast.error(e.response?.data?.detail || "Failed to load pages"))
        .finally(() => setLoading(false));
      return;
    }
    // Iter-62.2: opt-in paginated shape for admins.
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    if (searchTerm.trim()) params.set("q", searchTerm.trim());
    if (expFilter !== "all") params.set("form_experience", expFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (buyerFilter?.id) params.set("buyer_client_id", buyerFilter.id);
    // Fetch pages + analytics in parallel — analytics keyed by page.id.
    Promise.all([
      api.get(`/admin/custom-landing-pages?${params.toString()}`),
      api.get("/admin/custom-landing-pages/analytics?days=30").catch(() => ({ data: { by_page: {} } })),
    ])
      .then(([pRes, aRes]) => {
        const rows = pRes.data?.items || pRes.data?.pages || [];
        setPages(rows);
        setTotal(
          typeof pRes.data?.total === "number" ? pRes.data.total : rows.length,
        );
        setAnalytics(aRes.data?.by_page || {});
        // Iter-51: fetch readiness summary once — silent fail so an
        // outage on this endpoint doesn't break the table.
        api.post("/admin/custom-landing-pages/readiness/bulk",
                 { page_ids: rows.map((r) => r.id) })
          .then((rr) => setReadinessMap(rr.data?.readiness || {}))
          .catch(() => {});
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load custom pages"))
      .finally(() => setLoading(false));
  }, [isMediaBuyer, page, pageSize, searchTerm, expFilter, statusFilter, buyerFilter?.id]);

  React.useEffect(() => { load(); }, [load]);

  // Iter-62.2: reset to page 1 whenever any filter changes.
  React.useEffect(() => {
    setPage(1);
  }, [searchTerm, expFilter, statusFilter, buyerFilter?.id]);

  // Iter-62.2.2: sample-only fetch — narrow server-side query walks
  // every page (25/50/100 at a time) until the entire sample set is
  // collected. Explicit failure toast if any page request errors so
  // an incomplete Copy Sample URLs can never silently ship.
  React.useEffect(() => {
    if (isMediaBuyer) return;
    let cancelled = false;
    (async () => {
      const collected = [];
      const seen = new Set();
      const PAGE_SIZE = 100;
      try {
        let pageNo = 1;
        let total = 0;
        while (true) {
          const r = await api.get(
            `/admin/custom-landing-pages?sample_only=true&page=${pageNo}&page_size=${PAGE_SIZE}`,
          );
          if (cancelled) return;
          const items = r.data?.items || [];
          for (const it of items) {
            const key = it.id || it.slug;
            if (key && !seen.has(key)) {
              seen.add(key);
              collected.push(it);
            }
          }
          total = typeof r.data?.total === "number" ? r.data.total : collected.length;
          if (collected.length >= total || items.length === 0) break;
          pageNo += 1;
          // Safety brake — protects against pathological loops if the
          // backend reports total inconsistently. 100 pages × 100 =
          // 10 000 samples is far beyond any realistic seed set.
          if (pageNo > 100) break;
        }
        if (!cancelled) setSamplePages(collected);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.detail
            || "Failed to load ALL sample pages — Copy Sample URLs may be incomplete",
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isMediaBuyer, samplesReloadTick]);

  // Iter-75.8 — one-click "Restore samples" — hits the new backend
  // endpoint which un-deletes any soft-deleted `sample-*` pages and
  // inserts any missing ones. On success we bump the reload tick so the
  // samples useEffect re-fetches and the button counts update.
  const restoreSamples = React.useCallback(async () => {
    setRestoringSamples(true);
    try {
      const { data } = await api.post(
        "/admin/custom-landing-pages/restore-samples",
      );
      const parts = [];
      if (data.resurrected) parts.push(`${data.resurrected} restored`);
      if (data.created) parts.push(`${data.created} recreated`);
      const summary = parts.length ? parts.join(" · ") : "already up-to-date";
      toast.success(
        `Sample pages: ${summary} · ${data.total_samples_live} live`,
      );
      setSamplesReloadTick((t) => t + 1);
      // Also refresh the main pages table so the new rows show up if
      // the current filter includes them.
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringSamples(false);
    }
  }, [load]);

  // Iter-62.2.1: cross-component filter deep-link. The "Landing Page
  // Experiences" section (a sibling component) pushes ?custom_exp=quiz
  // into the URL when the admin clicks a "View Quiz pages" card, and
  // this effect syncs it into local filter state so the table below
  // scrolls into the correct filtered view.
  const [searchParams] = useSearchParams();
  React.useEffect(() => {
    const req = (searchParams.get("custom_exp") || "").toLowerCase();
    if (["all", "standard", "quiz", "focused_funnel"].includes(req)) {
      setExpFilter(req);
    }
  }, [searchParams]);

  const duplicate = async (p) => {
    setDuplicating(p.id);
    try {
      const r = await api.post(`/admin/custom-landing-pages/${p.id}/duplicate`);
      toast.success(`Duplicated as /lp/${r.data.slug}`);
      nav(`/admin/landing-pages/custom/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Duplicate failed");
    } finally {
      setDuplicating(null);
    }
  };

  const origin = publicOrigin();

  // Iter-53.1: One-click "Copy Sample URLs" — filters the page list for
  // the seeded sample slugs (Vance + the 6 iter-53.1 samples) and copies
  // a labeled block ready for pasting into an email or a Slack DM.
  //
  // Detection is by slug pattern so admins can add or remove samples
  // without touching this button. Uses the current `origin`, so clicking
  // in preview copies preview URLs and clicking on production copies
  // production URLs — no risk of accidentally sharing the wrong host.
  const sampleRows = React.useMemo(() => {
    const source = isMediaBuyer ? pages : samplePages;
    const isSample = (p) => (p.slug || "").startsWith("sample-");
    const groups = source.filter(isSample);
    groups.sort((a, b) => a.slug.localeCompare(b.slug));
    return groups;
  }, [pages, samplePages, isMediaBuyer]);

  const copySampleUrls = React.useCallback(() => {
    if (!sampleRows.length) {
      toast.error("No sample pages found yet — seed them on the next deploy");
      return;
    }
    // Iter-53.1d: build the per-buyer query-string once. Only sample-*
    // slugs honour ?firm= and ?phone= — Vance is a real client and
    // never accepts overrides.
    const qs = new URLSearchParams();
    const firm = (pitchFirm || "").trim();
    const phone = (pitchPhone || "").trim();
    if (firm) qs.set("firm", firm);
    if (phone) qs.set("phone", phone);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    // Iter-62.2.2: 3-way experience label — Standard / Quiz / Focused
    // Funnel. Prior 2-way "Quiz vs Long form" incorrectly bucketed
    // focused_funnel pages under "Long form".
    const expLabel = (p) => {
      const raw = (p.form_experience || "standard").toLowerCase();
      if (raw === "quiz") return "Quiz";
      if (raw === "focused_funnel") return "Focused Funnel";
      return "Standard";
    };
    const lines = sampleRows.map((p) => {
      const label = p.firm_name || p.slug;
      const tag = `${verticalLabel(p.vertical || "other")} · ${expLabel(p)}`;
      const url = `${origin}/lp/${p.slug}${suffix}`;
      return `• ${label} (${tag})\n  ${url}`;
    });
    const personalized = firm ? ` personalized for ${firm}` : "";
    const header = `LeadFlow — sample landing pages (${sampleRows.length})${personalized}:\n`;
    copy(header + lines.join("\n\n"));
  }, [sampleRows, origin, pitchFirm, pitchPhone]);

  // Iter-53.1c: "Send Buyer Pitch" — build a mailto: link with a
  // pre-drafted subject and body listing every sample URL grouped by
  // form-experience. Recipient stays blank so the user fills it in in
  // their own mail client. Vance is broken out as a "real client"
  // example to give buyers social proof.
  const sendBuyerPitch = React.useCallback(() => {
    if (!sampleRows.length) {
      toast.error("No sample pages found — deploy iter-53.1 seed first");
      return;
    }
    // Iter-53.1d: personalize sample URLs (never Vance).
    const qs = new URLSearchParams();
    const firm = (pitchFirm || "").trim();
    const phone = (pitchPhone || "").trim();
    if (firm) qs.set("firm", firm);
    if (phone) qs.set("phone", phone);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";

    // Iter-62.2.2: 3-way experience grouping — Standard / Quiz / Focused
    // Funnel. Prior 2-way "Long form vs Quiz" incorrectly buried
    // focused_funnel pages inside the Long-form group.
    const normalize = (p) => {
      const raw = (p.form_experience || "standard").toLowerCase();
      if (raw === "quiz") return "quiz";
      if (raw === "focused_funnel") return "focused_funnel";
      return "standard";
    };
    const standard = sampleRows.filter((p) => normalize(p) === "standard");
    const quiz = sampleRows.filter((p) => normalize(p) === "quiz");
    const focused = sampleRows.filter((p) => normalize(p) === "focused_funnel");

    const line = (p) => {
      const vertical = verticalLabel(p.vertical || "other");
      const norm = normalize(p);
      const suffixNote = norm === "quiz" ? " (60-sec quiz)"
        : norm === "focused_funnel" ? " (single-question funnel)"
        : "";
      return `• ${vertical}${suffixNote}: ${origin}/lp/${p.slug}${suffix}`;
    };

    const parts = [];
    const greeting = firm ? `Hi ${firm} team,` : "Hi there,";
    parts.push(
      greeting,
      "",
      "Thanks for taking a look at LeadFlow. Below are our sample "
      + "landing pages so you can see what we can build for your firm — "
      + (firm
        ? `each link is already re-branded with your firm name${phone ? " and phone" : ""} `
          + "so you can see how it looks live. "
        : "")
      + "every page is mobile-first with TCPA capture, TrustedForm "
      + "certification, and bot defense. Click any link to see it in "
      + "action.",
      "",
    );
    if (standard.length) {
      parts.push("Standard landing pages (traditional long-form intake):");
      for (const p of standard) parts.push(line(p));
      parts.push("");
    }
    if (quiz.length) {
      parts.push("Quiz-style landing pages (higher form completion for cold traffic):");
      for (const p of quiz) parts.push(line(p));
      parts.push("");
    }
    if (focused.length) {
      parts.push("Focused Funnel landing pages (single-question, high-intent conversion):");
      for (const p of focused) parts.push(line(p));
      parts.push("");
    }
    parts.push(
      "Happy to build a custom-branded version for your firm — usually "
      + "24-48 hours from your logo and brand colors to live. What time "
      + "works for a quick 15-min call this week?",
      "",
      "— [Your name]",
    );

    const subject = firm
      ? `LeadFlow — sample landing pages for ${firm}`
      : "LeadFlow — sample landing pages for your firm";
    const body = parts.join("\n");
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // Anchor + click is the most reliable cross-browser trigger for
    // mailto: — `window.location.href = mailto:` silently no-ops in
    // some Chromium builds and is uninterceptable by Playwright, so
    // we prefer a synthesized anchor. `window.location.href` remains
    // the fallback if for any reason the anchor path fails (e.g. the
    // page CSP forbids inline event dispatch).
    try {
      const a = document.createElement("a");
      a.href = href;
      a.rel = "noopener";
      a.style.display = "none";
      a.setAttribute("data-testid", "admin-custom-lp-pitch-mailto");
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 0);
    } catch {
      window.location.href = href;
    }
    toast.success(
      firm
        ? `Opening your email client — pitch personalized for ${firm}`
        : "Opening your email client with the pitch pre-drafted",
    );
  }, [sampleRows, origin, pitchFirm, pitchPhone]);

  return (
    <div className="brut-card mb-6" data-testid="admin-custom-lp-list" id="custom-landing-pages-anchor">
      <div className="border-b border-black px-4 py-3 flex items-center gap-2 flex-wrap">
        <Broadcast size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Custom buyer landing pages
        </div>
        <div className="label-mono ml-2">
          {isMediaBuyer
            ? "Attach or override marketing pixels per firm"
            : "Each page routes leads directly to one buyer — no state matching"}
        </div>
        {!isMediaBuyer && (
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {/* Iter-53.1d: personalise-sample inputs. When filled, the two
              green pitch buttons below append ?firm=…&phone=… to every
              sample URL — real-client Vance page is exempt. */}
          <div
            className="flex items-center gap-1 border-2 border-black bg-emerald-50 px-2 py-1"
            data-testid="admin-custom-lp-personalize"
            title="Personalize sample URLs with a firm name + phone."
          >
            <span className="text-[9px] uppercase tracking-widest text-emerald-900 font-bold">
              Personalize
            </span>
            <input
              type="text"
              value={pitchFirm}
              onChange={(e) => setPitchFirm(e.target.value)}
              placeholder="Firm name"
              data-testid="admin-custom-lp-pitch-firm"
              className="w-32 border border-black px-2 py-1 text-[11px] font-mono bg-white"
            />
            <input
              type="tel"
              value={pitchPhone}
              onChange={(e) => setPitchPhone(e.target.value)}
              placeholder="Phone"
              data-testid="admin-custom-lp-pitch-phone"
              className="w-28 border border-black px-2 py-1 text-[11px] font-mono bg-white"
            />
            {(pitchFirm || pitchPhone) && (
              <button
                type="button"
                onClick={() => { setPitchFirm(""); setPitchPhone(""); }}
                data-testid="admin-custom-lp-pitch-clear"
                title="Clear personalization"
                className="text-[10px] uppercase tracking-widest text-emerald-900 hover:text-black px-1"
              >
                Clear
              </button>
            )}
          </div>
          <button
            onClick={copySampleUrls}
            data-testid="admin-custom-lp-copy-samples"
            disabled={!sampleRows.length}
            className="border-2 border-black bg-indigo-600 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1"
            title={
              sampleRows.length
                ? `Copy all ${sampleRows.length} sample URLs to clipboard, labeled for buyer pitching`
                : "No sample pages found — deploy iter-53.1 seed to populate"
            }
          >
            <Copy size={11} weight="bold" /> Copy sample URLs ({sampleRows.length})
          </button>
          <button
            onClick={sendBuyerPitch}
            data-testid="admin-custom-lp-send-pitch"
            disabled={!sampleRows.length}
            className="border-2 border-black bg-fuchsia-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-fuchsia-800 disabled:opacity-40 inline-flex items-center gap-1"
            title="Opens your default email client with a full pitch pre-drafted — sample links grouped by form type"
          >
            <PaperPlaneTilt size={11} weight="bold" /> Send buyer pitch
          </button>
          {canShare && (
          <button
            onClick={() => setShowProspect(true)}
            data-testid="admin-custom-lp-prospect"
            className="border-2 border-black bg-yellow-400 text-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-yellow-300 inline-flex items-center gap-1 font-bold"
            title="Send a prospect a preview of what their landing page and thank-you page could look like"
          >
            <Eye size={11} weight="bold" /> Show a prospect
          </button>
          )}
          {canDelete && (
            <button
              onClick={() => setShowTrash(true)}
              data-testid="admin-custom-lp-deleted-open"
              className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
              title="Pages you deleted: restore them or delete them for good"
            >
              <Trash size={11} weight="bold" /> Deleted pages
            </button>
          )}
          {user?.role === "super_admin" && (
            <button
              onClick={restoreSamples}
              disabled={restoringSamples}
              data-testid="admin-custom-lp-restore-samples"
              className="border-2 border-black bg-amber-500 text-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-amber-600 disabled:opacity-40 inline-flex items-center gap-1 font-bold"
              title={
                sampleRows.length === 0
                  ? "No sample pages found — click to recreate them so the pitch buttons above work"
                  : "Restore any deleted sample pages + insert any missing ones (idempotent)"
              }
            >
              <ArrowCounterClockwise size={11} weight="bold" />
              {restoringSamples ? "Restoring…" : "Restore samples"}
            </button>
          )}
          <button
            onClick={() => setShowDeck(true)}
            disabled={!sampleRows.length}
            data-testid="admin-custom-lp-preview-deck"
            className="border-2 border-black bg-black text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1"
            title={
              sampleRows.length
                ? "Flip through every sample in a lightbox — personalize with firm + phone live"
                : "No samples to preview — restore them first"
            }
          >
            <Eye size={11} weight="bold" /> Preview deck ({sampleRows.length})
          </button>
          {user?.role === "super_admin" && (
            <button
              onClick={() => setShowPitchAnalytics(true)}
              data-testid="admin-custom-lp-pitch-analytics"
              className="border-2 border-black bg-emerald-600 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-emerald-700 inline-flex items-center gap-1 font-bold"
              title="Pitch → Live conversion analytics — pitches per sample + time-to-first-lead"
            >
              <ChartLineUp size={11} weight="bold" /> Pitch analytics
            </button>
          )}
          <button
            onClick={() => setShowPing(true)}
            data-testid="admin-sitemap-ping-open"
            className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
            title="Tell Bing / Yandex / IndexNow-participating engines to re-crawl your pages now"
          >
            <Lightning size={11} weight="bold" /> Ping search engines
          </button>
          <button
            onClick={() => setBulkSharing(true)}
            data-testid="admin-custom-lp-bulk-share"
            className="border-2 border-black bg-emerald-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-emerald-800 inline-flex items-center gap-1"
            title="Mint one preview link per landing page so you can send a full deck to a prospective client"
          >
            <PaperPlaneTilt size={11} weight="bold" /> Share ALL previews
          </button>
          <button
            onClick={() => nav("/admin/lp-analytics")}
            data-testid="admin-custom-lp-live-analytics"
            className="border-2 border-black bg-sky-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-sky-800 inline-flex items-center gap-1"
            title="GA-style impression + funnel counters on live /lp/ traffic"
          >
            <ChartLineUp size={11} weight="bold" /> Live traffic
          </button>
          <button
            onClick={() => setShowBulk(true)}
            data-testid="admin-custom-lp-bulk-open"
            className="border-2 border-black bg-amber-100 px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
            title="Create one landing page per case type at once"
          >
            <Stack size={11} weight="bold" /> Bulk create all verticals
          </button>
          <button
            onClick={() => setShowTpl(true)}
            data-testid="admin-custom-lp-from-template"
            className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
            title="Copy a shared page or another page for one buyer, then edit it"
          >
            <Stack size={11} weight="bold" /> Buyer page from template
          </button>
          <button
            onClick={() => nav("/admin/landing-pages/custom/new")}
            data-testid="admin-custom-lp-new"
            className="border border-black bg-black text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-1"
          >
            + New buyer page
          </button>
        </div>
        )}
      </div>
      {showBulk && (
        <BulkVerticalCreateModal
          onClose={() => setShowBulk(false)}
          onCreated={() => { setShowBulk(false); load(); }}
        />
      )}
      {showPing && (
        <SitemapPingModal onClose={() => setShowPing(false)} />
      )}
      {showTpl && <NewFromTemplateDialog onClose={() => setShowTpl(false)} />}
      {sharing && (
        <SharePreviewModal
          page={sharing}
          origin={origin}
          onClose={() => setSharing(null)}
        />
      )}
      {showProspect && (
        <ProspectPreviewModal
          samples={sampleRows}
          origin={origin}
          onClose={() => setShowProspect(false)}
          onDraftCreated={load}
        />
      )}
      {showTrash && (
        <DeletedPagesModal
          onClose={() => setShowTrash(false)}
          onChanged={() => { load(); setSamplesReloadTick((t) => t + 1); }}
        />
      )}
      {bulkSharing && (
        <BulkSharePreviewModal
          origin={origin}
          pages={pages}
          onClose={() => setBulkSharing(false)}
        />
      )}
      {pitchTarget && (
        <PitchToLiveModal
          samplePage={pitchTarget}
          onClose={() => setPitchTarget(null)}
          onCreated={() => { setPitchTarget(null); load(); setSamplesReloadTick((t) => t + 1); }}
        />
      )}
      {showDeck && sampleRows.length > 0 && (
        <SamplePreviewLightbox
          samples={sampleRows}
          origin={origin}
          onClose={() => setShowDeck(false)}
        />
      )}
      {showPitchAnalytics && (
        <PitchAnalyticsPanel onClose={() => setShowPitchAnalytics(false)} />
      )}
      <div className="p-4">
        {/* Iter-62.2: search + filter toolbar. Media Buyer skips this —
            their masked endpoint has no server-side filters. */}
        {!isMediaBuyer && (
          <div
            className="mb-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_1fr] gap-2"
            data-testid="admin-custom-lp-toolbar"
          >
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search firm, slug, or buyer…"
              data-testid="admin-custom-lp-search"
              className="border-2 border-black px-2 py-1.5 text-sm"
            />
            <select
              value={expFilter}
              onChange={(e) => setExpFilter(e.target.value)}
              data-testid="admin-custom-lp-experience-filter"
              className="border-2 border-black px-2 py-1.5 text-sm font-mono"
            >
              <option value="all">All experiences</option>
              <option value="standard">Standard</option>
              <option value="quiz">Quiz</option>
              <option value="focused_funnel">Focused Funnel</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-custom-lp-status-filter"
              className="border-2 border-black px-2 py-1.5 text-sm font-mono"
            >
              <option value="all">All statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="paused">Paused</option>
            </select>
            <div data-testid="admin-custom-lp-buyer-filter">
              <BuyerCombobox
                value={buyerFilter}
                onChange={setBuyerFilter}
                includeAll={true}
                allLabel="All buyers"
                placeholder="Filter by buyer…"
                testIdPrefix="admin-custom-lp-buyer"
              />
            </div>
          </div>
        )}
        {loading && <div className="label-mono text-center py-4">Loading…</div>}
        {!loading && !pages.length && (
          <div className="label-mono text-center py-6 text-zinc-500" data-testid="admin-custom-lp-empty">
            {(!isMediaBuyer && (searchTerm || expFilter !== "all" || statusFilter !== "all" || buyerFilter?.id))
              ? "No custom pages match those filters."
              : (<>
                No custom buyer pages yet.
                <br />
                <span className="text-[10px]">Build one for each firm — pick a hero image, write copy, publish. Traffic from Facebook/Google routes straight to them.</span>
              </>)}
          </div>
        )}
        {!loading && pages.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[960px]" data-testid="admin-custom-lp-table">
            <thead>
              <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Firm</th>
                <th className="text-left py-2 px-3">Vertical</th>
                <th className="text-left py-2 px-3">Slug</th>
                <th className="text-left py-2 px-3">Buyer destination</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">30d conversion</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id}
                    className="border-b border-zinc-200 hover:bg-zinc-50"
                    data-testid={`admin-custom-lp-row-${p.slug}`}>
                  <td className="py-2 px-3 font-bold">
                    <div className="inline-flex items-center gap-1.5 flex-wrap">
                      <span>{p.firm_name}</span>
                      {p.prospect_draft && (
                        <span
                          data-testid={`admin-custom-lp-prospect-chip-${p.slug}`}
                          title="A draft page made for a prospect. Connect a buyer and publish it to go live."
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border-2 border-yellow-500 bg-yellow-100 text-black font-bold"
                        >
                          Prospect
                        </span>
                      )}
                      <PixelBadge pageId={p.id} testId={`admin-custom-lp-pixel-${p.slug}`} />
                      {(p.is_sample_page
                        || (p.slug || "").startsWith("sample-")) && (
                        <span
                          data-testid={`admin-custom-lp-demo-chip-${p.slug}`}
                          title="Sample / demo landing page — used by Copy sample URLs and Send buyer pitch. Never routes real leads."
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border-2 border-amber-600 bg-amber-100 text-amber-900 font-bold"
                        >
                          Demo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className="inline-block text-[9px] uppercase tracking-widest border px-1.5 py-0.5"
                      style={{
                        borderColor: verticalColor(p.vertical || "other"),
                        color: verticalColor(p.vertical || "other"),
                      }}
                      data-testid={`admin-custom-lp-vertical-${p.slug}`}
                    >
                      {verticalLabel(p.vertical || "other")}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-[11px]">
                    <a href={pageLink(`/lp/${p.slug}${p.status !== "published" ? "?preview=1" : ""}`, p.status === "published")}
                       target="_blank" rel="noreferrer"
                       className="underline hover:text-emerald-700"
                       data-testid={`admin-custom-lp-open-${p.slug}`}>
                      /lp/{p.slug}
                    </a>
                  </td>
                  <td className="py-2 px-3 text-zinc-600">{p.buyer_destination_name || "—"}</td>
                  <td className="py-2 px-3">
                    <span className={
                      "text-[9px] uppercase tracking-widest px-1.5 py-0.5 border " +
                      (p.status === "published"
                        ? "bg-emerald-700 border-emerald-700 text-white"
                        : p.status === "paused"
                          ? "bg-red-600 border-red-600 text-white"
                          : "bg-amber-100 border-amber-700 text-amber-900")
                    } data-testid={`admin-custom-lp-status-${p.slug}`}>
                      {p.status}
                    </span>
                    {/* Iter-51: launch-readiness pill — silently hidden on
                        media_buyer rows (attribution view has its own scope)
                        and while readiness is still loading. */}
                    {!isMediaBuyer && readinessMap[p.id] && (
                      <span
                        title={
                          readinessMap[p.id].ready
                            ? "Launch readiness: READY"
                            : `${readinessMap[p.id].blocker_count} blocker${
                                readinessMap[p.id].blocker_count === 1 ? "" : "s"
                              }, ${readinessMap[p.id].warning_count} warning${
                                readinessMap[p.id].warning_count === 1 ? "" : "s"
                              }`
                        }
                        data-testid={`admin-custom-lp-readiness-${p.slug}`}
                        className={
                          "ml-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border " +
                          (readinessMap[p.id].ready
                            ? "bg-emerald-50 border-emerald-700 text-emerald-900"
                            : readinessMap[p.id].blocker_count > 0
                              ? "bg-red-50 border-red-700 text-red-900"
                              : "bg-amber-50 border-amber-700 text-amber-900")
                        }
                      >
                        {readinessMap[p.id].ready
                          ? "ready"
                          : `${readinessMap[p.id].blocker_count}⛔ ${readinessMap[p.id].warning_count}⚠`}
                      </span>
                    )}
                    {/* Iter-52 / Iter-62.2: Form Experience pill so admins
                        can tell at a glance whether a page renders as
                        Standard, Quiz, or Focused Funnel. A/B experiment
                        state is a SEPARATE badge below — never conflated
                        with the underlying experience. */}
                    {(() => {
                      const exp = (p.form_experience || "standard").toLowerCase();
                      const norm = (exp === "quiz" || exp === "focused_funnel")
                        ? exp : "standard";
                      const label = norm === "quiz" ? "Quiz"
                        : norm === "focused_funnel" ? "Focused Funnel"
                        : "Standard";
                      const cls = norm === "quiz"
                        ? "bg-violet-50 border-violet-700 text-violet-900"
                        : norm === "focused_funnel"
                          ? "bg-sky-50 border-sky-700 text-sky-900"
                          : "bg-zinc-50 border-zinc-500 text-zinc-700";
                      return (
                        <span
                          data-testid={`admin-custom-lp-form-experience-${p.slug}`}
                          title={`Form experience: ${label}`}
                          className={
                            "ml-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border " + cls
                          }
                        >{label}</span>
                      );
                    })()}
                    {/* Iter-62.2: A/B experiment badge — orthogonal to
                        the experience label above. */}
                    {Boolean(p.experiment_enabled)
                      && (p.experiment_quiz_weight ?? 50) > 0
                      && (p.experiment_standard_weight ?? 50) > 0 && (
                      <span
                        data-testid={`admin-custom-lp-experiment-${p.slug}`}
                        title={`A/B experiment: ${p.experiment_standard_weight ?? 50}/${p.experiment_quiz_weight ?? 50}`}
                        className="ml-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border bg-indigo-50 border-indigo-700 text-indigo-900"
                      >A/B {p.experiment_standard_weight ?? 50}/{p.experiment_quiz_weight ?? 50}</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <ConversionSparkline
                      stats={analytics[p.id]}
                      testId={`admin-custom-lp-spark-${p.slug}`}
                    />
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => nav(`/admin/landing-pages/custom/${p.id}`)}
                      data-testid={`admin-custom-lp-edit-${p.slug}`}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                    >
                      {isMediaBuyer ? "Pixels" : "Edit"}
                    </button>
                    {!isMediaBuyer && (
                    <button
                      onClick={() => duplicate(p)}
                      disabled={duplicating === p.id}
                      data-testid={`admin-custom-lp-duplicate-${p.slug}`}
                      className="ml-2 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:border-emerald-700 hover:text-white disabled:opacity-40"
                      title="Clone hero + content + branding into a fresh draft"
                    >
                      {duplicating === p.id ? "…" : "Duplicate"}
                    </button>
                    )}
                    {!isMediaBuyer && user?.role === "super_admin"
                      && (p.is_sample_page
                        || (p.slug || "").startsWith("sample-")) && (
                    <button
                      onClick={() => setPitchTarget(p)}
                      data-testid={`admin-custom-lp-pitch-live-${p.slug}`}
                      className="ml-2 border-2 border-black bg-emerald-700 text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-800 font-bold"
                      title="One-click: turn this sample into a real branded landing page for a specific firm"
                    >
                      Pitch → Live
                    </button>
                    )}
                    {!isMediaBuyer && user?.role === "super_admin"
                      && p.pitched_from_sample_id && (
                    <button
                      onClick={async () => {
                        const ok = window.confirm(
                          "Roll back this Pitch-to-Live?\n\n"
                          + `Firm: ${p.firm_name}\nSlug: /lp/${p.slug}\n\n`
                          + "This soft-deletes the page and — if nothing else "
                          + "references them — the auto-provisioned "
                          + "destination and client too. Only allowed if the "
                          + "page has never received real traffic."
                        );
                        if (!ok) return;
                        setRollingBack(p.id);
                        try {
                          const { data } = await api.post(
                            `/admin/custom-landing-pages/${p.id}/pitch-rollback`,
                          );
                          const bits = ["page removed"];
                          if (data.destination_deleted) bits.push("destination removed");
                          if (data.client_deleted) bits.push("client archived");
                          toast.success(`Rolled back — ${bits.join(" · ")}`);
                          load();
                          setSamplesReloadTick((t) => t + 1);
                        } catch (err) {
                          const d = err?.response?.data?.detail;
                          if (typeof d === "object" && d?.code === "pitch_rollback_blocked") {
                            toast.error(d.message);
                          } else {
                            toast.error(d || "Rollback failed");
                          }
                        } finally { setRollingBack(null); }
                      }}
                      disabled={rollingBack === p.id}
                      data-testid={`admin-custom-lp-pitch-rollback-${p.slug}`}
                      className="ml-2 border-2 border-black bg-white text-red-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white disabled:opacity-40 font-bold"
                      title="Undo pitch — soft-delete this page + auto-provisioned destination + client (only if no traffic)"
                    >
                      {rollingBack === p.id ? "…" : "Undo pitch"}
                    </button>
                    )}
                    {!isMediaBuyer && (
                    <button
                      onClick={() => setSharing(p)}
                      className="ml-2 border border-black bg-emerald-700 text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-800"
                      data-testid={`admin-custom-lp-share-${p.slug}`}
                      title="Get a shareable preview link for a prospective client"
                    >
                      Share preview
                    </button>
                    )}
                    <button
                      onClick={() => copy(withCode(`${origin}/lp/${p.slug}`, mbCode))}
                      className="ml-2 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                      data-testid={`admin-custom-lp-copy-${p.slug}`}
                    >
                      Copy URL
                    </button>
                    {/* Iter-62.2: Open Live vs Preview split — Open Live
                        opens the real /lp/{slug}; Preview appends
                        ?preview=1 so drafts are viewable without going
                        live and preview submissions never create a lead. */}
                    <a
                      href={pageLink(`/lp/${p.slug}`, p.status === "published")}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-block"
                      data-testid={`admin-custom-lp-open-live-${p.slug}`}
                      title="Open the real public URL"
                    >
                      Open live
                    </a>
                    <a
                      href={pageLink(`/lp/${p.slug}?preview=1`, p.status === "published")}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-block"
                      data-testid={`admin-custom-lp-preview-${p.slug}`}
                      title="Open with ?preview=1 — no lead created"
                    >
                      Preview
                    </a>
                    {!isMediaBuyer && canDelete && !p.is_global_funnel && (
                      <DeletePageButton
                        page={p}
                        submitted30d={analytics[p.id]?.submitted || 0}
                        onDeleted={() => { load(); setSamplesReloadTick((t) => t + 1); }}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {/* Iter-62.2: Pagination bar — admin only. */}
        {!isMediaBuyer && !loading && total > 0 && (
          <div
            className="mt-4 flex items-center justify-between gap-3 flex-wrap border-t border-black pt-3"
            data-testid="admin-custom-lp-pagination"
          >
            <div className="label-mono" data-testid="admin-custom-lp-page-info">
              Page {page} of {Math.max(1, Math.ceil(total / pageSize))} · {total} total
            </div>
            <div className="flex items-center gap-2">
              <label className="label-mono">Per page</label>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                data-testid="admin-custom-lp-page-size"
                className="border border-black px-2 py-1 text-xs font-mono"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="admin-custom-lp-prev"
                className="border border-black px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-black"
              >Prev</button>
              <button
                type="button"
                disabled={page * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
                data-testid="admin-custom-lp-next"
                className="border border-black px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-black"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Iter-46: Bulk vertical create modal.
 *
 * One firm, many verticals, one submit. Admin fills the shared config
 * (buyer destination, firm name, phone, brand color, TCPA, states) and
 * ticks which case types to spin up (defaults to all 5). Each vertical
 * gets its own custom LP with vertical-specific hero copy from the
 * canonical preset library. Pages can be individually edited or paused
 * afterwards.
 */
/**
 * Iter-47: Sitemap Ping modal — pushes URLs to IndexNow (Bing, Yandex,
 * Seznam, Naver, Yep). Shows the last 20 ping attempts + success/fail
 * indicator per row so admins can prove "yes I told Bing about this".
 *
 * Google no longer accepts sitemap pings as of 2023 — new pages there
 * are discovered via sitemap.xml on next crawl (typically hours-days).
 * The modal makes this trade-off visible so admins aren't confused when
 * pages don't appear in Google immediately.
 */
function SitemapPingModal({ onClose }) {
  const origin = publicOrigin();
  const [status, setStatus] = React.useState(null);
  const [pinging, setPinging] = React.useState(false);
  const [lastResult, setLastResult] = React.useState(null);

  const loadStatus = React.useCallback(() => {
    api.get("/admin/sitemap/ping/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ recent_pings: [], indexnow_configured: false }));
  }, []);

  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const doPing = async () => {
    setPinging(true);
    try {
      const r = await api.post("/admin/sitemap/ping", { urls: [] });
      setLastResult(r.data);
      const ok = r.data?.ok;
      toast[ok ? "success" : "warning"](
        ok
          ? `Pinged ${r.data.url_count} URLs · ${r.data.status} in ${r.data.latency_ms}ms`
          : `Push returned ${r.data.status} — check details`,
      );
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ping failed");
    } finally {
      setPinging(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="sitemap-ping-modal"
    >
      <div className="bg-white border-4 border-black max-w-3xl w-full shadow-[8px_8px_0_0_#f1b62c]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <Lightning size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            Ping search engines — IndexNow
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="sitemap-ping-close"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          >✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed">
            <div className="label-mono mb-1">What this does</div>
            Pushes every published landing page URL to <b>IndexNow</b> so{" "}
            <b>Bing, Yandex, Seznam, Naver, and Yep</b> re-crawl them in
            minutes instead of days. Google does NOT support sitemap
            pings anymore — for Google, submit your sitemap once at{" "}
            <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer"
               className="underline">Search Console → Sitemaps</a> (link:{" "}
            <code className="font-mono">{landerOrigin() ? `${landerOrigin()}/sitemap.xml` : `${origin}/api/sitemap.xml`}</code>).
          </div>

          {status && !status.indexnow_configured && (
            <div className="border-2 border-red-700 bg-red-50 p-3 text-[11px]">
              <b>IndexNow key not configured.</b> Ask ops to set{" "}
              <code className="font-mono">INDEXNOW_KEY</code> in{" "}
              <code className="font-mono">backend/.env</code>.
            </div>
          )}

          <button
            onClick={doPing}
            disabled={pinging || (status && !status.indexnow_configured)}
            data-testid="sitemap-ping-fire"
            className="w-full border-2 border-black bg-emerald-700 text-white px-4 py-3 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-800 inline-flex items-center justify-center gap-2"
          >
            <Lightning size={14} weight="bold" />
            {pinging ? "Pinging…" : "Ping every published page now"}
          </button>

          {lastResult && (
            <div className={
              "border-2 p-3 text-[11px] " +
              (lastResult.ok ? "border-emerald-700 bg-emerald-50" : "border-red-700 bg-red-50")
            } data-testid="sitemap-ping-last-result">
              <div className="font-bold mb-1">
                {lastResult.ok ? "✓ Push accepted" : `✗ Push returned ${lastResult.status}`}
              </div>
              <div className="font-mono">
                {lastResult.url_count} URLs · {lastResult.latency_ms}ms
                {lastResult.note ? ` · note: ${lastResult.note}` : ""}
              </div>
            </div>
          )}

          <div>
            <div className="label-mono mb-2">Last 20 pings</div>
            <div className="border border-black divide-y divide-zinc-200 max-h-64 overflow-y-auto" data-testid="sitemap-ping-history">
              {!status && <div className="p-3 text-[11px] font-mono text-zinc-500">Loading…</div>}
              {status && !status.recent_pings.length && (
                <div className="p-3 text-[11px] font-mono text-zinc-500">No pings yet.</div>
              )}
              {status && status.recent_pings.map((p, i) => (
                <div key={i} className="p-2 text-[10px] font-mono flex items-center gap-2">
                  <span className={
                    "inline-block w-2 h-2 rounded-full " +
                    (p.status_code >= 200 && p.status_code < 300
                      ? "bg-emerald-600" : "bg-red-600")
                  } />
                  <span className="text-zinc-500">{(p.created_at || "").replace("T", " ").slice(0, 16)}</span>
                  <span className="font-bold">HTTP {p.status_code}</span>
                  <span className="text-zinc-500">· {p.url_count} URLs</span>
                  <span className="text-zinc-500">· {p.latency_ms}ms</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function BulkVerticalCreateModal({ onClose, onCreated }) {
  const [destinations, setDestinations] = React.useState(null); // null = loading
  const [saving, setSaving] = React.useState(false);
  const [showNewDest, setShowNewDest] = React.useState(false);
  const [newDestSlot, setNewDestSlot] = React.useState("primary");
  const destLoading = destinations === null;
  const [form, setForm] = React.useState({
    buyer_destination_id: "",
    cc_destination_ids: [],
    firm_name: "",
    verticals: [...VERTICAL_ORDER], // all by default
    phone: "",
    brand_color: "#111111",
    tcpa_language: TCPA_LANGUAGE,
    state_allowlist: [],
    thankyou_headline: "",
    thankyou_message: "",
    thankyou_channels: ["call", "text", "email"],
    publish_immediately: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleVertical = (v) => {
    setForm((f) => ({
      ...f,
      verticals: f.verticals.includes(v)
        ? f.verticals.filter((x) => x !== v)
        : [...f.verticals, v],
    }));
  };

  React.useEffect(() => {
    api.get("/delivery/destinations")
      .then((r) => setDestinations((r.data?.destinations || r.data || []).filter((d) => d.is_active)))
      .catch(() => setDestinations([]));
  }, []);

  const submit = async () => {
    if (!form.buyer_destination_id) return toast.error("Pick a buyer destination");
    if (!form.firm_name.trim()) return toast.error("Firm name is required");
    if (!form.verticals.length) return toast.error("Pick at least one vertical");
    setSaving(true);
    try {
      const r = await api.post("/admin/custom-landing-pages/bulk-verticals", form);
      const n = r.data?.count || 0;
      toast.success(`Created ${n} landing page${n === 1 ? "" : "s"}${form.publish_immediately ? " — LIVE" : " (drafts)"}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="bulk-vertical-modal"
    >
      <div className="bg-white border-4 border-black max-w-4xl w-full shadow-[8px_8px_0_0_#f1b62c]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <Stack size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            Bulk create — one page per case type
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bulk-vertical-cancel"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          >✕</button>
        </div>

        <div className="p-6 space-y-5">
          <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed">
            <b>One firm, five case types.</b> Fill the shared config once — we
            build a landing page per vertical (auto, truck, rideshare, motorcycle,
            general PI). Slugs auto-derive as{" "}
            <code className="font-mono">/lp/{"<firm>"}-auto</code>,{" "}
            <code className="font-mono">/lp/{"<firm>"}-truck</code>, etc. Each
            page is fully editable afterwards.
          </div>

          {/* Verticals to create */}
          <div>
            <div className="label-mono mb-2">Which case types?</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="bulk-vertical-list">
              {VERTICAL_ORDER.map((k) => {
                const preset = VERTICAL_PRESETS[k];
                const active = form.verticals.includes(k);
                return (
                  <label
                    key={k}
                    data-testid={`bulk-vertical-${k}`}
                    className={
                      "border-2 p-3 cursor-pointer flex items-start gap-3 transition " +
                      (active
                        ? "border-black bg-white shadow-[3px_3px_0_0_" + preset.color + "]"
                        : "border-zinc-400 bg-zinc-50 hover:border-black hover:bg-white")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleVertical(k)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-chivo font-black uppercase tracking-tighter text-sm">
                        {preset.label}
                      </div>
                      <div className="label-mono">{preset.tagline}</div>
                      <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">
                        &ldquo;{preset.headline}&rdquo;
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Shared config */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-mono">Buyer destination (all pages route here) <span className="text-red-700">*</span></div>
              <div className="flex items-center gap-2 mt-1">
                <select
                  value={form.buyer_destination_id}
                  onChange={(e) => set("buyer_destination_id", e.target.value)}
                  disabled={destLoading}
                  data-testid="bulk-dest"
                  className="flex-1 border border-black px-3 py-2 text-sm font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {destLoading ? "Loading destinations…" : "— Pick a buyer destination —"}
                  </option>
                  {(destinations || [])
                    .filter((d) => !(form.cc_destination_ids || []).includes(d.id))
                    .map((d) => (
                      <option key={d.id} value={d.id}>{d.name} · {d.type}</option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setNewDestSlot("primary"); setShowNewDest(true); }}
                  data-testid="bulk-new-dest"
                  className="border-2 border-black bg-white px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
                >
                  <Plus size={11} weight="bold" /> New
                </button>
              </div>
              {/* Iter-54: two optional CC destinations, same policy as
                  single-LP editor — fan-out on every submission. */}
              <div className="mt-2 space-y-2">
                {[0, 1].map((slotIdx) => {
                  const ccIds = form.cc_destination_ids || [];
                  const currentId = ccIds[slotIdx] || "";
                  const excluded = new Set([
                    form.buyer_destination_id,
                    ...ccIds.filter((_, i) => i !== slotIdx),
                  ]);
                  const options = (destinations || []).filter((d) => !excluded.has(d.id));
                  return (
                    <div key={slotIdx} className="flex items-center gap-2">
                      <select
                        value={currentId}
                        onChange={(e) => {
                          const next = [...ccIds];
                          next[slotIdx] = e.target.value;
                          set("cc_destination_ids", next.filter(Boolean));
                        }}
                        data-testid={`bulk-cc-dest-${slotIdx}`}
                        disabled={!form.buyer_destination_id}
                        className="flex-1 border border-zinc-400 px-3 py-2 text-xs font-mono disabled:opacity-50"
                      >
                        <option value="">
                          {form.buyer_destination_id
                            ? `— Optional CC destination ${slotIdx + 1} —`
                            : "Pick primary first"}
                        </option>
                        {options.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} · {d.type}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => { setNewDestSlot(`cc-${slotIdx}`); setShowNewDest(true); }}
                        data-testid={`bulk-new-cc-${slotIdx}`}
                        disabled={!form.buyer_destination_id}
                        className="border border-black bg-white px-2 py-2 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1 disabled:opacity-40 whitespace-nowrap"
                      >
                        <Plus size={10} weight="bold" /> New
                      </button>
                    </div>
                  );
                })}
                <div className="text-[10px] text-zinc-500">
                  Fan out every submission to the primary <b>and</b> each CC destination.
                </div>
              </div>
            </div>
            <div>
              <div className="label-mono">Firm name (display) <span className="text-red-700">*</span></div>
              <input
                value={form.firm_name}
                onChange={(e) => set("firm_name", e.target.value)}
                placeholder="Firm name"
                data-testid="bulk-firm"
                className="w-full border border-black px-3 py-2 mt-1 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="label-mono">Phone (top bar)</div>
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="1-800-555-0199"
                data-testid="bulk-phone"
                className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
              />
            </div>
            <div>
              <div className="label-mono">Brand color</div>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={form.brand_color}
                       onChange={(e) => set("brand_color", e.target.value)}
                       className="w-10 h-10 border border-black" />
                <input value={form.brand_color}
                       onChange={(e) => set("brand_color", e.target.value)}
                       className="flex-1 border border-black px-3 py-2 text-sm font-mono" />
              </div>
            </div>
            <div>
              <div className="label-mono">Go live on save?</div>
              <label className="flex items-center gap-2 mt-1 border border-black px-3 py-2 cursor-pointer bg-white hover:bg-amber-50">
                <input
                  type="checkbox"
                  checked={form.publish_immediately}
                  onChange={(e) => set("publish_immediately", e.target.checked)}
                  data-testid="bulk-publish"
                />
                <span className="text-sm">
                  {form.publish_immediately
                    ? "🟢 Publish all pages immediately"
                    : "📝 Keep as drafts (I'll review first)"}
                </span>
              </label>
            </div>
          </div>

          <div>
            <div className="label-mono">
              TCPA consent language (recorded verbatim on every lead)
            </div>
            <textarea
              value={form.tcpa_language || ""}
              onChange={(e) => set("tcpa_language", e.target.value)}
              rows={4}
              data-testid="bulk-tcpa"
              className="w-full border border-black px-3 py-2 mt-1 text-[11px] font-mono"
            />
          </div>

          <div>
            <div className="label-mono">
              Thank-you message (shown after every submission)
            </div>
            <textarea
              value={form.thankyou_message || ""}
              onChange={(e) => set("thankyou_message", e.target.value)}
              rows={3}
              placeholder="A member of our intake team will call you within 24 hours from area code (205). Please save the number so you don't miss the call."
              data-testid="bulk-thankyou-msg"
              className="w-full border border-black px-3 py-2 mt-1 text-sm"
            />
          </div>

          <div className="flex items-center gap-2 pt-3 border-t-2 border-black">
            <button
              onClick={submit}
              disabled={saving}
              data-testid="bulk-submit"
              className="border-2 border-black bg-emerald-700 text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-800 inline-flex items-center gap-2"
            >
              <Stack size={13} weight="bold" />
              {saving
                ? "Creating…"
                : `Create ${form.verticals.length} page${form.verticals.length === 1 ? "" : "s"}`}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border border-black px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Iter-54: inline destination-creator (same widget as the single-LP editor). */}
      {showNewDest && (
        <NewDestinationModal
          onClose={() => setShowNewDest(false)}
          onCreated={(dest) => {
            setDestinations((prev) => [dest, ...(prev || [])]);
            if (newDestSlot === "primary") {
              set("buyer_destination_id", dest.id);
            } else {
              const idx = Number(newDestSlot.split("-")[1] || 0);
              const next = [...(form.cc_destination_ids || [])];
              next[idx] = dest.id;
              set("cc_destination_ids", next.filter(Boolean));
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Iter-48: Photo Retention widget.
 *
 * Shows the admin how much accident-photo evidence is stored, how much
 * is due to be deleted on the next nightly sweep, and lets them:
 *   - preview what would be deleted (dry-run)
 *   - trigger a real sweep now (short-circuits waiting for the cron)
 *
 * The retention window itself (PHOTO_RETENTION_DAYS) lives in the
 * backend .env and is intentionally not editable from the UI — that's
 * a compliance-sensitive value that should require ops involvement.
 */
function PhotoRetentionSection() {
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [dryPreview, setDryPreview] = React.useState(null);
  const [restorable, setRestorable] = React.useState(null);
  const [restoringId, setRestoringId] = React.useState(null);

  const load = React.useCallback(() => {
    api.get("/admin/photo-retention/status")
      .then((r) => setState(r.data))
      .catch(() => setState({ stats: null, recent_sweeps: [] }));
  }, []);

  const loadRestorable = React.useCallback(() => {
    api.get("/admin/photo-retention/restorable")
      .then((r) => setRestorable(r.data))
      .catch(() => setRestorable({ restorable: [], count: 0 }));
  }, []);

  React.useEffect(() => { load(); loadRestorable(); }, [load, loadRestorable]);

  const restore = async (fileId) => {
    setRestoringId(fileId);
    try {
      await api.post(`/admin/photo-retention/restore/${fileId}`);
      toast.success("Photo restored — visible on the lead again.");
      loadRestorable(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't restore this photo");
    } finally {
      setRestoringId(null);
    }
  };

  const runSweep = async (dryRun) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/photo-retention/sweep", { dry_run: dryRun });
      if (dryRun) {
        setDryPreview(r.data);
        toast.message(
          `Dry-run: would soft-delete ${r.data.soft_deleted_estimate ?? r.data.examined ?? 0} photos.`,
        );
      } else {
        setDryPreview(null);
        const sd = r.data.soft_deleted ?? 0;
        const hp = r.data.hard_purged ?? 0;
        const f  = r.data.storage_failures ?? 0;
        toast.success(
          `Soft-deleted ${sd}${sd === 1 ? " photo" : " photos"} · ` +
          `hard-purged ${hp}` + (f ? ` · ${f} storage failure${f === 1 ? "" : "s"}` : ""),
        );
        load(); loadRestorable();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sweep failed");
    } finally {
      setBusy(false);
    }
  };

  const stats = state?.stats;
  const enabled = stats?.enabled;

  return (
    <div className="brut-card mb-6" data-testid="photo-retention-section">
      <div className="border-b border-black px-4 py-3 flex items-center gap-2 flex-wrap">
        <HardDrives size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Photo retention
        </div>
        <div className="label-mono ml-2">
          Nightly sweep soft-deletes accident photos older than{" "}
          {stats ? `${stats.retention_days} days` : "…"}, then hard-purges after{" "}
          {stats ? `${stats.grace_hours}h grace` : "…"}. Restore is possible during grace.
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => runSweep(true)}
            disabled={busy || !enabled}
            data-testid="photo-retention-preview"
            className="border border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
          >
            {busy ? "…" : "Preview (dry-run)"}
          </button>
          <button
            onClick={() => runSweep(false)}
            disabled={busy || !enabled
              || (stats && stats.due_for_deletion === 0 && stats.due_to_purge === 0)}
            data-testid="photo-retention-sweep"
            className="border-2 border-black bg-red-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-red-800 disabled:opacity-40 inline-flex items-center gap-1"
          >
            <Trash size={11} weight="bold" /> Run cleanup now
          </button>
        </div>
      </div>

      <div className="p-4">
        {!stats && (
          <div className="label-mono text-center py-4 text-zinc-500">Loading…</div>
        )}

        {stats && !enabled && (
          <div className="border-2 border-amber-600 bg-amber-50 p-3 text-[12px]" data-testid="photo-retention-disabled">
            <b>Retention disabled.</b> Set <code className="font-mono">PHOTO_RETENTION_DAYS</code>{" "}
            to a positive number (e.g. 60) in <code className="font-mono">backend/.env</code>{" "}
            to re-enable the nightly sweep. Use disable during a legal-hold
            / discovery period.
          </div>
        )}

        {stats && enabled && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="photo-retention-stats">
            <StatTile label="Window"          value={`${stats.retention_days}d`} />
            <StatTile label="Grace"           value={`${stats.grace_hours}h`} />
            <StatTile label="Stored"          value={stats.active_photos} />
            <StatTile label="Restorable"      value={stats.restorable}
                      accent={stats.restorable > 0 ? "violet" : "black"} />
            <StatTile label="Deleted (30d)"   value={stats.deleted_last_30d} />
          </div>
        )}

        {/* Iter-49: Restorable photos list */}
        {restorable?.count > 0 && (
          <div className="mt-4 border-4 border-violet-700 bg-violet-50 p-4" data-testid="photo-restore-list">
            <div className="font-chivo font-black uppercase tracking-tighter text-sm mb-2 inline-flex items-center gap-2">
              <ArrowCounterClockwise size={14} weight="bold" className="text-violet-700" />
              Restorable — {restorable.count} photo{restorable.count === 1 ? "" : "s"} in the grace window
            </div>
            <div className="label-mono mb-3">
              These photos were soft-deleted but the bytes are still in
              storage. Click <b>Restore</b> to un-delete before the grace
              window expires and the next hard-purge removes them permanently.
            </div>
            <div className="border border-black divide-y divide-zinc-200 bg-white">
              {restorable.restorable.map((r) => {
                const remainingMs = new Date(r.purge_after).getTime() - Date.now();
                const remainingHours = Math.max(0, Math.floor(remainingMs / 3600000));
                const remainingMinutes = Math.max(0, Math.floor((remainingMs % 3600000) / 60000));
                const urgent = remainingHours < 2;
                return (
                  <div key={r.file_id} className="p-3 flex items-center gap-3 flex-wrap text-[11px]" data-testid={`photo-restore-row-${r.file_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-chivo font-black uppercase tracking-tighter text-sm">
                        {r.filename || "(unnamed)"}
                      </div>
                      <div className="font-mono text-zinc-500">
                        {r.lead_label || (r.lead_id ? `lead ${r.lead_id.slice(0, 8)}…` : "(orphaned)")}
                      </div>
                    </div>
                    <div className={"font-mono " + (urgent ? "text-red-700 font-bold" : "text-zinc-600")}>
                      {remainingHours >= 1
                        ? `${remainingHours}h ${remainingMinutes}m left`
                        : `${remainingMinutes}m left`}
                      {urgent && " ⚠"}
                    </div>
                    <button
                      onClick={() => restore(r.file_id)}
                      disabled={restoringId === r.file_id}
                      data-testid={`photo-restore-btn-${r.file_id}`}
                      className="border-2 border-violet-700 bg-white text-violet-700 px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-violet-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
                    >
                      <ArrowCounterClockwise size={11} weight="bold" />
                      {restoringId === r.file_id ? "…" : "Restore"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {dryPreview && (
          <div className="mt-4 border-2 border-violet-700 bg-violet-50 p-3 text-[11px]" data-testid="photo-retention-dry-preview">
            <b>Dry-run result:</b> {dryPreview.examined} photo(s) would be
            touched (cutoff: {(dryPreview.cutoff || "").slice(0, 10)}).{" "}
            <button
              className="ml-2 underline"
              onClick={() => setDryPreview(null)}
            >dismiss</button>
          </div>
        )}

        {state?.recent_sweeps?.length > 0 && (
          <div className="mt-4">
            <div className="label-mono mb-2">Recent sweeps</div>
            <div className="border border-black divide-y divide-zinc-200" data-testid="photo-retention-history">
              {state.recent_sweeps.map((s, i) => (
                <div key={i} className="p-2 text-[10px] font-mono flex items-center gap-2 flex-wrap">
                  <span className={
                    "inline-block w-2 h-2 rounded-full " +
                    (s.storage_failures > 0 ? "bg-amber-500" : "bg-emerald-600")
                  } />
                  <span className="text-zinc-500">{(s.created_at || "").replace("T", " ").slice(0, 16)}</span>
                  <span className="font-bold">Soft {s.soft_deleted ?? s.deleted ?? 0}</span>
                  <span className="font-bold">· Purged {s.hard_purged ?? 0}</span>
                  {s.storage_failures > 0 && (
                    <span className="text-amber-700">· {s.storage_failures} storage fail{s.storage_failures === 1 ? "" : "s"}</span>
                  )}
                  <span className="text-zinc-500">· {s.latency_ms}ms</span>
                  <span className="text-zinc-500">· by {s.actor_id === "cron" ? "cron" : "admin"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Iter-54.2 — Sample Submissions Dashboard.
 *
 * Companion to Iter-54.1 Safe Demo Mode: sample landing-page submissions
 * are captured in `sample_page_submissions` (PII-free) so ops can see
 * demo traffic (buyer walkthroughs, click tests) without contaminating
 * the real leads pipeline. Rows older than 30 days are auto-purged by
 * the hourly `/api/cron/data-retention` cron.
 */
function SampleSubmissionsSection() {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    setBusy(true);
    api.get("/admin/sample-submissions?limit=20")
      .then((r) => setData(r.data))
      .catch(() => setData({ submissions: [], count: 0, total: 0 }))
      .finally(() => setBusy(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const rows = data?.submissions || [];
  const total = data?.total || 0;

  const fmtDt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const short = (s, n = 42) => {
    if (!s) return "—";
    return s.length > n ? `${s.slice(0, n)}…` : s;
  };

  return (
    <div className="brut-card mb-6" data-testid="sample-submissions-section">
      <div className="border-b border-black px-4 py-3 flex items-center gap-2 flex-wrap">
        <TestTube size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Sample submissions (demo traffic)
        </div>
        <div className="label-mono ml-2">
          Safe Demo Mode — no real leads, no delivery, no pixel firing.
          Rows &gt; 30d auto-purged.
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span
            data-testid="sample-submissions-total"
            className="text-[10px] uppercase tracking-widest border border-black bg-white px-2 py-1"
          >
            {total} total
          </span>
          <button
            onClick={load}
            disabled={busy}
            data-testid="sample-submissions-refresh"
            className="border border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
          >
            {busy ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="p-4">
        {!data && (
          <div className="label-mono text-center py-4 text-zinc-500">Loading…</div>
        )}
        {data && rows.length === 0 && (
          <div className="label-mono text-center py-6 text-zinc-500" data-testid="sample-submissions-empty">
            No demo submissions yet. Every /lp/sample-* form submit lands here
            (title-cased slug, IP + user-agent for observability). Real
            landing pages are unaffected.
          </div>
        )}
        {rows.length > 0 && (
          <div className="border border-black bg-white overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-zinc-100 border-b border-black">
                <tr className="text-left uppercase tracking-widest text-[9px]">
                  <th className="py-2 px-3">When</th>
                  <th className="py-2 px-3">Slug</th>
                  <th className="py-2 px-3">State</th>
                  <th className="py-2 px-3">Demo ref</th>
                  <th className="py-2 px-3">IP</th>
                  <th className="py-2 px-3">User-agent</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.demo_ref}-${i}`} className="border-b border-zinc-200" data-testid={`sample-submissions-row-${i}`}>
                    <td className="py-2 px-3 whitespace-nowrap text-[10px]">{fmtDt(r.created_at)}</td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <span className="border border-black px-1.5 py-0.5 bg-amber-50 text-[10px]">{r.slug || "—"}</span>
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">{r.state || "—"}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{r.demo_ref || "—"}</td>
                    <td className="py-2 px-3 whitespace-nowrap text-zinc-600">{r.ip || "—"}</td>
                    <td className="py-2 px-3 text-zinc-500" title={r.user_agent || ""}>{short(r.user_agent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



function StatTile({ label, value, accent }) {
  const border =
    accent === "amber"  ? "border-amber-600" :
    accent === "green"  ? "border-emerald-600" :
    accent === "violet" ? "border-violet-700" :
    "border-black";
  return (
    <div className={`border-2 ${border} bg-white p-3`}>
      <div className="label-mono">{label}</div>
      <div className="font-chivo font-black text-2xl tracking-tighter mt-1">{value}</div>
    </div>
  );
}


/**
 * Iter-50: Share Preview modal — mints time-limited, revocable client
 * preview tokens so admins can walk a prospective buyer through both
 * the landing page AND the Thank-You page WITHOUT logging in and
 * WITHOUT creating a real lead in the pipeline.
 *
 * Flow:
 *   - Modal opens with a list of existing active tokens for THIS page.
 *   - "Generate preview link" mints a fresh token, auto-selects it, and
 *     immediately renders the shareable LP + Thank-You URLs.
 *   - Copy buttons put each URL on the clipboard for pasting into
 *     email/Slack/etc.
 *   - Revoke button kills a link early (e.g. deal fell through).
 *
 * Tokens are opaque randoms — the DB only stores the SHA-256 hash so a
 * leaked DB dump can't be replayed. The raw token is returned ONCE at
 * mint time; if the admin needs to re-share, they mint a new one.
 */
function SharePreviewModal({ page, origin, onClose }) {
  const [tokens, setTokens] = React.useState(null); // null = loading
  const [minting, setMinting] = React.useState(false);
  const [revokingId, setRevokingId] = React.useState(null);
  const [ttlDays, setTtlDays] = React.useState(14);
  const [label, setLabel] = React.useState("");
  // The raw token is only present at creation time — stash it here so we
  // can render the shareable URLs. Refreshing the token list clears it
  // because the DB only stores the hash.
  const [freshToken, setFreshToken] = React.useState(null);

  const load = React.useCallback(() => {
    api.get(`/admin/custom-landing-pages/${page.id}/preview-tokens?include_revoked=1`)
      .then((r) => setTokens(r.data?.tokens || []))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Failed to load preview links");
        setTokens([]);
      });
  }, [page.id]);

  React.useEffect(() => { load(); }, [load]);

  const mint = async () => {
    setMinting(true);
    try {
      const r = await api.post(
        `/admin/custom-landing-pages/${page.id}/preview-tokens`,
        { ttl_days: ttlDays, label: label.trim() || null },
      );
      setFreshToken(r.data);
      setLabel("");
      toast.success(`Preview link ready · valid ${ttlDays} day${ttlDays === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't mint preview link");
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (tokenId) => {
    if (!window.confirm("Kill this preview link? Anyone who has it will get a 404 immediately.")) return;
    setRevokingId(tokenId);
    try {
      await api.post(`/admin/custom-landing-pages/preview-tokens/${tokenId}/revoke`);
      toast.success("Preview link revoked.");
      if (freshToken?.id === tokenId) setFreshToken(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revoke failed");
    } finally {
      setRevokingId(null);
    }
  };

  // Build the two shareable URLs from the fresh raw token. The list
  // rows below only expose "Revoke" / age info because we no longer have
  // the raw string once the modal reloads.
  const lpUrl = freshToken?.token
    ? `${origin}/lp/${page.slug}?demo=${encodeURIComponent(freshToken.token)}`
    : "";
  const tyUrl = freshToken?.token
    ? `${origin}/lp/thank-you?slug=${encodeURIComponent(page.slug)}&demo=${encodeURIComponent(freshToken.token)}${page.firm_name ? `&firm=${encodeURIComponent(page.firm_name)}` : ""}${page.phone ? `&phone=${encodeURIComponent(page.phone)}` : ""}`
    : "";

  const fmtRelative = (isoDate) => {
    if (!isoDate) return "";
    const ms = new Date(isoDate).getTime() - Date.now();
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    if (ms < 0) return "expired";
    if (days >= 1) return `${days}d ${hours}h left`;
    if (hours >= 1) return `${hours}h left`;
    return `<1h left`;
  };

  const activeTokens = (tokens || []).filter((t) => !t.revoked_at
                                                     && new Date(t.expires_at) > new Date());
  const staleTokens  = (tokens || []).filter((t) => t.revoked_at
                                                     || new Date(t.expires_at) <= new Date());

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="share-preview-modal"
    >
      <div className="bg-white border-4 border-black max-w-3xl w-full shadow-[8px_8px_0_0_#059669]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <PaperPlaneTilt size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            Share preview — {page.firm_name || page.slug}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-preview-close"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          >✕</button>
        </div>

        <div className="p-6 space-y-5">
          <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed">
            <b>Send a prospective client a link to walk their landing page + Thank-You page.</b>{" "}
            The link works even for draft / paused pages, hides internal admin UI, and
            blocks the form so no real lead gets created. Tokens expire on their own —
            revoke early if a deal falls through.
          </div>

          {/* Mint a new preview link */}
          <div className="border-2 border-emerald-700 bg-emerald-50 p-4" data-testid="share-preview-mint">
            <div className="font-chivo font-black uppercase tracking-tighter text-sm mb-3 inline-flex items-center gap-2">
              <PaperPlaneTilt size={14} weight="bold" className="text-emerald-700" />
              Generate a new preview link
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="label-mono">Label (optional)</label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Sent to a prospect — 2/12"
                  data-testid="share-preview-label"
                  maxLength={120}
                  className="w-full border border-black px-3 py-2 mt-1 text-sm"
                />
              </div>
              <div>
                <label className="label-mono">Valid for</label>
                <select
                  value={ttlDays}
                  onChange={(e) => setTtlDays(Number(e.target.value))}
                  data-testid="share-preview-ttl"
                  className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                >
                  <option value={1}>1 day</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                </select>
              </div>
              <button
                onClick={mint}
                disabled={minting}
                data-testid="share-preview-mint-btn"
                className="border-2 border-black bg-black text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-2 whitespace-nowrap"
              >
                <PaperPlaneTilt size={13} weight="bold" />
                {minting ? "Creating…" : "Generate link"}
              </button>
            </div>

            {freshToken && (
              <div className="mt-4 space-y-3" data-testid="share-preview-fresh">
                <div>
                  <div className="label-mono mb-1 inline-flex items-center gap-2">
                    <Eye size={11} weight="bold" /> Landing page preview
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 font-mono text-[11px] break-all border border-black bg-white px-2 py-1.5"
                      data-testid="share-preview-lp-url"
                    >{lpUrl}</div>
                    <button
                      onClick={() => copy(lpUrl)}
                      data-testid="share-preview-lp-copy"
                      className="border border-black px-2 py-1.5 hover:bg-black hover:text-white transition-colors"
                      title="Copy landing page preview URL"
                    ><Copy size={12} weight="bold" /></button>
                    <a
                      href={lpUrl} target="_blank" rel="noreferrer"
                      data-testid="share-preview-lp-open"
                      className="border border-black px-2 py-1.5 hover:bg-black hover:text-white transition-colors"
                      title="Open in a new tab"
                    ><ArrowSquareOut size={12} weight="bold" /></a>
                  </div>
                </div>
                <div>
                  <div className="label-mono mb-1 inline-flex items-center gap-2">
                    <Eye size={11} weight="bold" /> Thank-You page preview
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 font-mono text-[11px] break-all border border-black bg-white px-2 py-1.5"
                      data-testid="share-preview-ty-url"
                    >{tyUrl}</div>
                    <button
                      onClick={() => copy(tyUrl)}
                      data-testid="share-preview-ty-copy"
                      className="border border-black px-2 py-1.5 hover:bg-black hover:text-white transition-colors"
                      title="Copy Thank-You preview URL"
                    ><Copy size={12} weight="bold" /></button>
                    <a
                      href={tyUrl} target="_blank" rel="noreferrer"
                      data-testid="share-preview-ty-open"
                      className="border border-black px-2 py-1.5 hover:bg-black hover:text-white transition-colors"
                      title="Open in a new tab"
                    ><ArrowSquareOut size={12} weight="bold" /></a>
                  </div>
                </div>
                <div className="text-[10px] font-mono text-zinc-600">
                  This token is shown ONCE. Save the URLs above — refreshing the list
                  will not reveal the token again. Mint another if you need to re-share.
                </div>
              </div>
            )}
          </div>

          {/* Active tokens */}
          <div>
            <div className="label-mono mb-2">
              Active preview links ({activeTokens.length})
            </div>
            <div className="border border-black divide-y divide-zinc-200" data-testid="share-preview-active-list">
              {tokens === null && (
                <div className="p-3 text-[11px] font-mono text-zinc-500">Loading…</div>
              )}
              {tokens !== null && activeTokens.length === 0 && (
                <div className="p-3 text-[11px] font-mono text-zinc-500">
                  No active preview links. Generate one above.
                </div>
              )}
              {activeTokens.map((t) => (
                <div
                  key={t.id}
                  className="p-3 text-[11px] flex items-center gap-3 flex-wrap"
                  data-testid={`share-preview-row-${t.id}`}
                >
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-chivo font-black uppercase tracking-tighter text-sm">
                      {t.label || "(unlabelled)"}
                    </div>
                    <div className="font-mono text-zinc-500 text-[10px]">
                      minted {(t.created_at || "").replace("T", " ").slice(0, 16)}
                      {t.created_by_email ? ` · by ${t.created_by_email}` : ""}
                    </div>
                  </div>
                  <div className="font-mono text-zinc-600">
                    {t.view_count || 0} view{(t.view_count || 0) === 1 ? "" : "s"}
                    {t.last_viewed_at && (
                      <span className="text-emerald-700">
                        {" "}· last {(t.last_viewed_at || "").replace("T", " ").slice(0, 16)}
                      </span>
                    )}
                  </div>
                  <div className="font-mono">
                    {fmtRelative(t.expires_at)}
                  </div>
                  <button
                    onClick={() => revoke(t.id)}
                    disabled={revokingId === t.id}
                    data-testid={`share-preview-revoke-${t.id}`}
                    className="border-2 border-red-700 bg-white text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
                    title="Kill this preview link immediately"
                  >
                    <Prohibit size={10} weight="bold" />
                    {revokingId === t.id ? "…" : "Revoke"}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {staleTokens.length > 0 && (
            <details className="text-[11px]" data-testid="share-preview-stale">
              <summary className="cursor-pointer label-mono">
                Show expired / revoked ({staleTokens.length})
              </summary>
              <div className="mt-2 border border-zinc-300 divide-y divide-zinc-200 bg-zinc-50">
                {staleTokens.map((t) => (
                  <div key={t.id} className="p-2 flex items-center gap-3 text-zinc-500 font-mono text-[10px]">
                    <div className="flex-1">
                      {t.label || "(unlabelled)"} — {t.revoked_at ? "revoked" : "expired"}
                    </div>
                    <div>{t.view_count || 0} view{(t.view_count || 0) === 1 ? "" : "s"}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Iter-50.1: Bulk Share-Preview modal — one click mints a preview token
 * for every custom landing page and renders the full landing + Thank-You
 * URL matrix so an admin can paste the whole deck into an email in one go.
 *
 * Design decisions:
 *   - Mint happens on modal open (auto-run) — the flow only makes sense
 *     as a one-shot. Once minted, urls are stashed in local state so
 *     the raw tokens never need to be re-requested.
 *   - Optional status filter lets an admin scope to drafts (e.g. showing
 *     a client every unfinished page before publish) or published-only
 *     (e.g. audit already-live URLs from one place).
 *   - "Copy all as text" builds a plain-text block ready to paste into
 *     Gmail / Slack. "Copy all as CSV" is the same info in a spreadsheet-
 *     friendly shape for buyers who prefer that.
 */
function BulkSharePreviewModal({ origin, pages, onClose }) {
  const [ttlDays, setTtlDays] = React.useState(14);
  const [label, setLabel] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [minting, setMinting] = React.useState(false);
  const [result, setResult] = React.useState(null); // { count, expires_at, tokens: [...] }
  // Personalise every generated URL for the specific prospective buyer.
  // Sample-* landing pages read ?firm= / ?phone= on any surface. Real
  // client pages (e.g. vance-al-injury) also honor these params, but
  // ONLY when the URL carries a valid `demo=<token>` preview link (which
  // every URL minted in this modal does) — so public/organic traffic to
  // /lp/vance-al-injury can never be cosmetically spoofed via a bare
  // firm= in the URL. See CustomLandingPage.jsx `allowBrandOverride`.
  const [buyerFirm, setBuyerFirm] = React.useState("");
  const [buyerPhone, setBuyerPhone] = React.useState("");

  const availableStatuses = React.useMemo(() => {
    // Iter-62.2.1: Share-ALL modal must NOT derive available statuses
    // from the current paginated page (only shows 25 of N). The
    // lifecycle enum is small and fixed — hardcode the three valid
    // statuses so the dropdown reflects the full dataset.
    return ["published", "draft", "paused"];
  }, []);

  const mint = async () => {
    setMinting(true);
    try {
      const body = { ttl_days: ttlDays, label: label.trim() || null };
      if (statusFilter !== "all") body.status_filter = [statusFilter];
      const r = await api.post(
        "/admin/custom-landing-pages/preview-tokens/bulk", body,
      );
      setResult(r.data);
      toast.success(`Minted ${r.data.count} preview link${r.data.count === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk mint failed");
    } finally {
      setMinting(false);
    }
  };

  const rowsWithUrls = React.useMemo(() => {
    if (!result?.tokens) return [];
    const trimmedFirm = buyerFirm.trim();
    const trimmedPhone = buyerPhone.trim();
    const personalised = Boolean(trimmedFirm || trimmedPhone);
    return result.tokens.map((t) => {
      // Prospective-buyer overrides win over the page's own firm/phone
      // when provided. Otherwise fall back to whatever the page carries
      // (which is how the Thank-You URL has always been personalised).
      const firmValue = trimmedFirm || t.firm_name || "";
      const phoneValue = trimmedPhone || t.phone || "";
      const firmQP = firmValue ? `&firm=${encodeURIComponent(firmValue)}` : "";
      const phoneQP = phoneValue ? `&phone=${encodeURIComponent(phoneValue)}` : "";
      // Sample-* landing pages honour ?firm= / ?phone= on any surface;
      // real client pages honour them ONLY when a preview surface is
      // active (see Iter-53.1d + Iter-54.2c). Every URL minted in this
      // modal carries a valid `demo=<token>`, so appending firm/phone
      // to the LP URL personalizes every row — including Vance —
      // without weakening the anti-spoof guard for public/organic
      // traffic. Only include the params when the operator has
      // explicitly typed a prospective-buyer firm/phone; a bare bulk
      // share of un-personalised links still renders each sample page
      // with its own catalogue branding.
      const lpQP = personalised ? `${firmQP}${phoneQP}` : "";
      return {
        ...t,
        firm_display: trimmedFirm || t.firm_name || t.page_slug,
        phone_display: trimmedPhone || t.phone || "",
        // Iter-57: badge label per row so the pitched buyer can tell
        // each experience apart at a glance. Falls back to "Standard"
        // for pages that pre-date the form_experience field.
        experience_label: (() => {
          const fx = (t.form_experience || "standard").toLowerCase();
          if (fx === "focused_funnel") return "Focused Funnel";
          if (fx === "quiz") return "Quiz";
          return "Standard";
        })(),
        personalised,
        lp_url: `${origin}/lp/${t.page_slug}?demo=${encodeURIComponent(t.token)}${lpQP}`,
        ty_url: `${origin}/lp/thank-you?slug=${encodeURIComponent(t.page_slug)}&demo=${encodeURIComponent(t.token)}${firmQP}${phoneQP}`,
      };
    });
  }, [result, origin, buyerFirm, buyerPhone]);

  const copyAllText = () => {
    if (!rowsWithUrls.length) return;
    const ttl = result?.ttl_days || ttlDays;
    const trimmedFirm = buyerFirm.trim();
    const personalHeader = trimmedFirm
      ? `Preview links personalised for ${trimmedFirm} `
      : "Preview links ";
    const header =
      `${personalHeader}(valid ${ttl} days · expires ${(result?.expires_at || "").slice(0, 10)})\n` +
      `═══════════════════════════════════════════════════════════\n\n`;
    const body = rowsWithUrls
      .map((r) => (
        `${r.firm_display} [${r.experience_label}] (${r.status || "draft"})\n` +
        `  Landing:    ${r.lp_url}\n` +
        `  Thank-You:  ${r.ty_url}\n`
      ))
      .join("\n");
    copy(header + body);
  };

  const copyAllCsv = () => {
    if (!rowsWithUrls.length) return;
    const header = "firm_name,slug,status,vertical,form_experience,landing_page_url,thank_you_url,expires_at";
    const rows = rowsWithUrls.map((r) => [
      JSON.stringify(r.firm_display || ""),
      r.page_slug,
      r.status || "",
      r.vertical || "",
      r.experience_label || "Standard",
      r.lp_url,
      r.ty_url,
      r.expires_at || "",
    ].join(","));
    copy([header, ...rows].join("\n"));
  };

  const eligibleCount = statusFilter === "all"
    ? (pages || []).length
    : (pages || []).filter((p) => p.status === statusFilter).length;

  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="bulk-share-preview-modal"
    >
      <div className="bg-white border-4 border-black max-w-5xl w-full shadow-[8px_8px_0_0_#059669]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <PaperPlaneTilt size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            Share ALL preview links
          </div>
          <div className="label-mono ml-2">
            One preview token per landing page — send the whole deck in one paste
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bulk-share-preview-close"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          >✕</button>
        </div>

        <div className="p-6 space-y-5">
          {!result && (
            <>
              <div className="border border-black bg-amber-50 p-3 text-[11px] leading-relaxed">
                <b>Generate a preview URL for every custom landing page in one go.</b>{" "}
                Perfect for a discovery call where a client wants to see how each of
                their firm&apos;s campaigns will look. Every link expires on its own
                and can be revoked per-page from the individual &ldquo;Share preview&rdquo; modal.
              </div>

              {/* Iter-54.2b: Personalise every URL for a specific
                  prospective buyer — sample-* pages will re-render
                  with this firm name + phone. Real client pages
                  ignore the params so no branding can be spoofed. */}
              <div
                className="border-2 border-emerald-700 bg-emerald-50 p-3"
                data-testid="bulk-share-personalize"
              >
                <div className="label-mono mb-2 inline-flex items-center gap-1">
                  <PaperPlaneTilt size={11} weight="bold" className="text-emerald-800" />
                  Personalise for prospective buyer <span className="text-zinc-500">(optional — sample pages re-render with this firm name + phone)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-2 items-center">
                  <input
                    value={buyerFirm}
                    onChange={(e) => setBuyerFirm(e.target.value)}
                    placeholder="e.g. Smith & Ward Injury Attorneys"
                    data-testid="bulk-share-buyer-firm"
                    maxLength={120}
                    className="w-full border border-black px-3 py-2 text-sm"
                  />
                  <input
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                    placeholder="e.g. 1-888-555-9101"
                    data-testid="bulk-share-buyer-phone"
                    maxLength={40}
                    className="w-full border border-black px-3 py-2 text-sm font-mono"
                  />
                  {(buyerFirm || buyerPhone) && (
                    <button
                      type="button"
                      onClick={() => { setBuyerFirm(""); setBuyerPhone(""); }}
                      data-testid="bulk-share-buyer-clear"
                      className="border border-emerald-700 text-emerald-800 px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white whitespace-nowrap"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="label-mono">Label (optional)</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Bulk demo for a prospect — 2/14"
                    data-testid="bulk-share-label"
                    maxLength={120}
                    className="w-full border border-black px-3 py-2 mt-1 text-sm"
                  />
                </div>
                <div>
                  <label className="label-mono">Valid for</label>
                  <select
                    value={ttlDays}
                    onChange={(e) => setTtlDays(Number(e.target.value))}
                    data-testid="bulk-share-ttl"
                    className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                  >
                    <option value={1}>1 day</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                  </select>
                </div>
                <div>
                  <label className="label-mono">Status filter</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="bulk-share-status"
                    className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                  >
                    <option value="all">All ({(pages || []).length})</option>
                    {availableStatuses.map((s) => (
                      <option key={s} value={s}>
                        {s} ({(pages || []).filter((p) => p.status === s).length})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={mint}
                  disabled={minting || eligibleCount === 0}
                  data-testid="bulk-share-mint-btn"
                  className="border-2 border-black bg-emerald-700 text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-800 inline-flex items-center gap-2 whitespace-nowrap"
                >
                  <PaperPlaneTilt size={13} weight="bold" />
                  {minting ? "Minting…" : `Generate ${eligibleCount} link${eligibleCount === 1 ? "" : "s"}`}
                </button>
              </div>
              {eligibleCount === 0 && (
                <div className="text-[11px] text-red-700 font-mono">
                  No landing pages match the current filter.
                </div>
              )}
            </>
          )}

          {result && (
            <>
              <div className="flex items-center gap-2 flex-wrap border-b-2 border-black pb-3">
                <div className="font-chivo font-black uppercase tracking-tighter text-sm">
                  {result.count} preview link{result.count === 1 ? "" : "s"} ready
                </div>
                <div className="label-mono">
                  Valid for {result.ttl_days} day{result.ttl_days === 1 ? "" : "s"}
                  {" "}· expires {(result.expires_at || "").slice(0, 10)}
                </div>
                {(buyerFirm.trim() || buyerPhone.trim()) && (
                  <div
                    className="text-[10px] uppercase tracking-widest border-2 border-emerald-700 bg-emerald-50 text-emerald-900 px-2 py-1"
                    data-testid="bulk-share-personalized-badge"
                  >
                    Personalised · {buyerFirm.trim() || "no firm"}
                    {buyerPhone.trim() ? ` · ${buyerPhone.trim()}` : ""}
                  </div>
                )}
                <div className="ml-auto flex gap-2 flex-wrap">
                  <button
                    onClick={copyAllText}
                    data-testid="bulk-share-copy-all-text"
                    className="border-2 border-black bg-black text-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-1"
                  >
                    <Copy size={11} weight="bold" /> Copy all as text
                  </button>
                  <button
                    onClick={copyAllCsv}
                    data-testid="bulk-share-copy-all-csv"
                    className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
                  >
                    <Copy size={11} weight="bold" /> Copy as CSV
                  </button>
                  <button
                    onClick={() => setResult(null)}
                    data-testid="bulk-share-reset"
                    className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                  >
                    ↺ Mint another batch
                  </button>
                </div>
              </div>

              <div className="border border-black bg-amber-50 p-2 text-[10px] font-mono">
                Tokens are shown ONCE. Save these URLs — refreshing loses the raw tokens.
              </div>

              <div className="border border-black divide-y divide-zinc-200 max-h-[520px] overflow-y-auto" data-testid="bulk-share-list">
                {rowsWithUrls.map((r) => (
                  <div key={r.id} className="p-3 space-y-2" data-testid={`bulk-share-row-${r.page_slug}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-chivo font-black uppercase tracking-tighter text-sm flex-1 min-w-0">
                        {r.firm_display}
                      </div>
                      {r.experience_label && (
                        <span
                          className={
                            "text-[9px] uppercase tracking-widest px-1.5 py-0.5 border " +
                            (r.experience_label === "Focused Funnel"
                              ? "bg-sky-100 border-sky-700 text-sky-900"
                              : r.experience_label === "Quiz"
                                ? "bg-amber-100 border-amber-700 text-amber-900"
                                : "bg-zinc-100 border-zinc-500 text-zinc-800")
                          }
                          data-testid={`bulk-share-experience-${r.page_slug}`}
                        >
                          {r.experience_label}
                        </span>
                      )}
                      {r.personalised && (
                        <span
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-700 bg-emerald-50 text-emerald-900"
                          data-testid={`bulk-share-personalized-${r.page_slug}`}
                        >
                          Personalised
                        </span>
                      )}
                      <span className={
                        "text-[9px] uppercase tracking-widest px-1.5 py-0.5 border " +
                        (r.status === "published"
                          ? "bg-emerald-700 border-emerald-700 text-white"
                          : r.status === "paused"
                            ? "bg-red-600 border-red-600 text-white"
                            : "bg-amber-100 border-amber-700 text-amber-900")
                      }>{r.status}</span>
                      <span className="label-mono">/lp/{r.page_slug}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <div className="label-mono mb-1 inline-flex items-center gap-1">
                          <Eye size={10} weight="bold" /> Landing page
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 font-mono text-[10px] break-all border border-black bg-zinc-50 px-2 py-1">
                            {r.lp_url}
                          </div>
                          <button
                            onClick={() => copy(r.lp_url)}
                            data-testid={`bulk-share-copy-lp-${r.page_slug}`}
                            className="border border-black px-1.5 py-1 hover:bg-black hover:text-white"
                            title="Copy landing page URL"
                          ><Copy size={11} weight="bold" /></button>
                          <a
                            href={r.lp_url} target="_blank" rel="noreferrer"
                            data-testid={`bulk-share-open-lp-${r.page_slug}`}
                            className="border border-black px-1.5 py-1 hover:bg-black hover:text-white"
                            title="Open in a new tab"
                          ><ArrowSquareOut size={11} weight="bold" /></a>
                        </div>
                      </div>
                      <div>
                        <div className="label-mono mb-1 inline-flex items-center gap-1">
                          <Eye size={10} weight="bold" /> Thank-You page
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 font-mono text-[10px] break-all border border-black bg-zinc-50 px-2 py-1">
                            {r.ty_url}
                          </div>
                          <button
                            onClick={() => copy(r.ty_url)}
                            data-testid={`bulk-share-copy-ty-${r.page_slug}`}
                            className="border border-black px-1.5 py-1 hover:bg-black hover:text-white"
                            title="Copy Thank-You URL"
                          ><Copy size={11} weight="bold" /></button>
                          <a
                            href={r.ty_url} target="_blank" rel="noreferrer"
                            data-testid={`bulk-share-open-ty-${r.page_slug}`}
                            className="border border-black px-1.5 py-1 hover:bg-black hover:text-white"
                            title="Open in a new tab"
                          ><ArrowSquareOut size={11} weight="bold" /></a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

